// src/services/TransactionService.js - Enhanced with Stripe Connect & Tax
import { db, auth } from '../firebase/config';
import NotificationService from './NotificationService';
import { formatOrderDetailsMessage } from '../utils/orderFormConfig';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  getDoc, 
  serverTimestamp, 
  setDoc,
  query,
  where,
  getDocs,
  limit,
  increment
} from 'firebase/firestore';

const BACKEND_URL = '/api';
const PLATFORM_FEE_PERCENTAGE = 4; // ✅ CHANGED from 6 to 5

const TRANSACTION_FEE_PERCENTAGE = parseFloat(process.env.REACT_APP_TRANSACTION_FEE_PERCENTAGE) || 7;
const DEFAULT_TAX_RATE = parseFloat(process.env.REACT_APP_DEFAULT_TAX_RATE) || 0.08;

const USE_MOCK_STRIPE = false; // Set to false when ready for real Stripe

const PAYMENT_METHOD_LABELS = { zelle: 'Zelle', venmo: 'Venmo', cash: 'Cash', card: 'Card' };

// Read current Stripe mode from Firestore (test | live)
const getStripeMode = async () => {
  try {
    const snap = await getDoc(doc(db, 'config', 'stripeMode'));
    return snap.exists() ? (snap.data().mode || 'live') : 'live';
  } catch {
    return process.env.REACT_APP_STRIPE_MODE || 'live';
  }
};

const shouldUseMockStripe = async (sellerId) => {
  try {
    const [shopSnap, stripeMode] = await Promise.all([
      getDoc(doc(db, 'shops', sellerId)),
      getStripeMode()
    ]);

    if (!shopSnap.exists()) {
      console.log('❌ Shop not found, using mock');
      return true;
    }

    const shopData = shopSnap.data();
    const hasAccountId = (
      shopData.stripeAccountId &&
      typeof shopData.stripeAccountId === 'string' &&
      shopData.stripeAccountId.startsWith('acct_')
    );

    // In test mode: any valid acct_ ID is enough — the backend will use test keys.
    // In live mode: also require stripeAccountStatus === 'active'.
    const hasValidAccount = stripeMode === 'test'
      ? hasAccountId
      : (hasAccountId && shopData.stripeAccountStatus === 'active');

    console.log('✅ Stripe check result:', {
      sellerId,
      stripeMode,
      hasValidAccount,
      accountId: shopData.stripeAccountId?.substring(0, 20) + '...',
      status: shopData.stripeAccountStatus
    });

    return !hasValidAccount;
  } catch (error) {
    console.error('Error checking Stripe status:', error);
    return true;
  }
};



// Calculate comprehensive transaction costs
export const calculateTransactionCosts = (unitPrice, quantity) => {
  const subtotal = unitPrice * quantity;
  const total = subtotal; // Customer pays only the item price
  
  // 7% total fee split: Platform and Stripe share this
  const totalFeePercentage = 0.07; // 7%
  const totalFees = total * totalFeePercentage;
  
  // Actual Stripe fee (2.9% + $0.30)
  const stripeFee = (total * 0.029) + 0.30;
  
  // Platform gets remainder of the 7% after Stripe's actual fee
  const platformFee = Math.max(0, totalFees - stripeFee);
  
  // Seller receives 93% of total (or total minus all fees)
  const sellerPayout = total - totalFees;
  
  return {
    subtotal: parseFloat(subtotal.toFixed(2)),
    transactionFee: 0, // No longer charged to customer
    salesTax: 0, // No longer charged to customer
    total: parseFloat(total.toFixed(2)), // Same as subtotal now
    platformFee: parseFloat(platformFee.toFixed(2)),
    stripeFee: parseFloat(stripeFee.toFixed(2)),
    sellerPayout: parseFloat(sellerPayout.toFixed(2)),
    totalFeePercentage: 7 // For display purposes
  };
};

// Client-only "chat" shaped exactly like the real chats/{id} doc, keyed by a temp id,
// so the chat body can render instantly on click while the real Firestore writes are in flight.
export const buildOptimisticChat = ({
  item, sellerId, sellerName, unitPrice, requestedQuantity = 1, isService = false,
  buyerId, buyerName, buyerProfilePic = null, isGuest = false, customization = null,
  sellerShopPic = null,
}) => {
  const tempId = `pending-${Date.now()}`;
  const itemName = item?.name || '';
  const costs = calculateTransactionCosts(parseFloat(unitPrice) || 0, requestedQuantity);

  return {
    id: tempId,
    transactionId: tempId,
    chatId: tempId,
    itemId: item?.id,
    isService,
    itemName,
    itemImage: item?.images?.[0] || null,
    itemAddress: item?.address || null,
    buyerId,
    buyerName,
    sellerId,
    sellerName,
    participants: [buyerId, sellerId],
    roles: { [buyerId]: 'buyer', [sellerId]: 'seller' },
    role: 'buyer', // ChatView reads chat.role directly for a couple of guest-order checks
    isSelfOrder: buyerId === sellerId,
    isGuest,
    isGuestOrder: isGuest,
    paymentMethod: 'card',
    title: 'New Order',
    chatImage: buyerProfilePic || null,
    sellerShopPic: sellerShopPic || null,
    lastMessage: `Hello, I'd like to order ${requestedQuantity} ${isService ? 'slot(s) for' : 'x'} ${itemName}. Is this available?`,
    lastMessageTime: new Date(),
    unreadCount: 0,
    status: 'pending_seller_acceptance', // local-only convenience field; real doc only has pendingPurchase.status
    transactionStatus: 'pending_seller_acceptance', // mirrors MessagesPage's flattening of real chat docs
    pendingPurchase: {
      itemId: item?.id,
      isService,
      itemName,
      unitPrice: parseFloat(unitPrice) || 0,
      requestedQuantity: parseInt(requestedQuantity) || 1,
      ...costs,
      status: 'pending_seller_acceptance',
      buyerName,
      availableQuantity: 999,
      customization: customization || null,
    },
    __pendingCreation: true,
  };
};

export const TransactionService = {
  
calculateCartTotal: (cartItems) => {
  const subtotal = cartItems.reduce((sum, item) => 
    sum + (item.unitPrice * item.quantity), 0
  );
  
  const total = subtotal; // Customer pays only item prices
  
  // 7% total fee split between platform and Stripe
  const totalFeePercentage = 0.07;
  const totalFees = total * totalFeePercentage;
  
  const stripeFee = (total * 0.029) + 0.30;
  const platformFee = Math.max(0, totalFees - stripeFee);
  const sellerPayout = total - totalFees; // Seller gets 93%
  
  return {
    subtotal: parseFloat(subtotal.toFixed(2)),
    transactionFee: 0,
    salesTax: 0,
    total: parseFloat(total.toFixed(2)),
    platformFee: parseFloat(platformFee.toFixed(2)),
    stripeFee: parseFloat(stripeFee.toFixed(2)),
    sellerPayout: parseFloat(sellerPayout.toFixed(2)),
    totalFeePercentage: 7
  };
},

  initiateGuestTransaction: async (itemId, sellerId, unitPrice, requestedQuantity, meetupType, guestInfo, isService = false) => {
  try {
    console.log('🎭 Initiating GUEST transaction via API:', {
      itemId,
      sellerId,
      guestInfo,
      isService
    });

    // Validate guest info
    if (!guestInfo || !guestInfo.guestId || !guestInfo.phoneNumber) {
      throw new Error('Invalid guest information');
    }

    // Call backend API to create guest order
    const response = await fetch('/api/create-guest-order', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        itemId,
        sellerId,
        unitPrice: parseFloat(unitPrice),
        requestedQuantity: parseInt(requestedQuantity),
        meetupType,
        guestInfo: {
          guestId: guestInfo.guestId,
          firstName: guestInfo.firstName,
          phoneNumber: guestInfo.phoneNumber,
          displayName: guestInfo.displayName
        },
        isService: isService || false
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create guest order');
    }

    const result = await response.json();

    console.log('✅ Guest order created via API:', result);

    return {
      transactionId: result.transactionId,
      status: result.status,
      costs: result.costs,
      useMockStripe: result.useMockStripe,
      sellerStripeConnected: !result.useMockStripe,
      isService: result.isService,
      isGuestOrder: true,
      expiresAt: new Date(result.expiresAt)
    };

  } catch (error) {
    console.error('Error initiating guest transaction:', error);
    throw error;
  }
},

  // Step 1: Buyer initiates
initiateQuantityTransaction: async (itemId, sellerId, unitPrice, requestedQuantity, meetupType, isService = false, customization = null) => {
  try {
    const buyer = auth.currentUser;
    
    // ✅ SIMPLIFIED: Works for both authenticated and anonymous users
    if (!buyer) {
      throw new Error('Please sign in to continue');
    }

    // Self-orders allowed — user can order from their own shop

    // Check if user is guest (anonymous)
    const isGuest = buyer.isAnonymous;
    
    console.log('🛒 Initiating transaction:', {
      buyerId: buyer.uid,
      isGuest,
      isAnonymous: buyer.isAnonymous
    });

    // Get shop data
    const shopRef = doc(db, 'shops', sellerId);
    const shopSnap = await getDoc(shopRef);

    if (!shopSnap.exists()) {
      throw new Error('Shop not found');
    }

    const shopData = shopSnap.data();

    const sellerStripeAccountId = shopData.stripeAccountId || null;
    const sellerStripeStatus = shopData.stripeAccountStatus || 'not_connected';

    const stripeMode = await getStripeMode();
    const hasAccountId = (
      sellerStripeAccountId &&
      typeof sellerStripeAccountId === 'string' &&
      sellerStripeAccountId.startsWith('acct_')
    );
    // In test mode any acct_ ID is valid — backend uses test keys.
    // In live mode the account must be fully active.
    const hasValidStripeAccount = stripeMode === 'test'
      ? hasAccountId
      : (hasAccountId && sellerStripeStatus === 'active');

    // ⚠️ Mock payment flow forced for all transactions for now, regardless of Stripe status.
    const useMock = true;

    console.log('🔍 Payment setup - DETAILED CHECK:', {
      sellerId,
      stripeMode,
      rawStripeAccountId: shopData.stripeAccountId,
      rawStripeStatus: shopData.stripeAccountStatus,
      sellerStripeAccountId,
      sellerStripeStatus,
      hasAccountId,
      hasValidStripeAccount,
      WILL_USE_MOCK: useMock,
      decision: useMock ? '❌ USING MOCK' : '✅ USING REAL STRIPE (test keys)'
    });

    console.log('🔍 Looking for item/service:', {
      itemId,
      isService,
      servicesCount: shopData.services?.length || 0,
      itemsCount: shopData.items?.length || 0,
      allServiceIds: shopData.services?.map(s => s.id) || [],
      allItemIds: shopData.items?.map(i => i.id) || []
    });

    // Handle both items and services
    const productArray = isService ? shopData.services : shopData.items;
    
    // Add fallback - if not found in expected array, try the other one
    let item = productArray?.find(p => p.id === itemId);
    
    if (!item && isService) {
      console.warn('⚠️ Service not found in services array, checking items array...');
      item = shopData.items?.find(p => p.id === itemId);
      if (item) {
        console.log('✅ Found in items array - treating as service');
        isService = false;
      }
    } else if (!item && !isService) {
      console.warn('⚠️ Item not found in items array, checking services array...');
      item = shopData.services?.find(p => p.id === itemId);
      if (item) {
        console.log('✅ Found in services array - treating as service');
        isService = true;
      }
    }
    
    if (!item) {
      console.error('❌ Item/Service not found in either array:', {
        searchedId: itemId,
        isService,
        services: shopData.services,
        items: shopData.items
      });
      throw new Error(`${isService ? 'Service' : 'Item'} not found`);
    }

    console.log('✅ Found item/service:', {
      id: item.id,
      name: item.name,
      isService,
      hasSlots: item.hasOwnProperty('slots'),
      hasQuantity: item.hasOwnProperty('quantity')
    });

    // Use 'slots' for services, 'quantity' for items
    const availableQuantity = parseInt(isService ? item.slots : item.quantity) || 0;
    
    if (availableQuantity < 1) {
      throw new Error(`This ${isService ? 'service' : 'item'} is out of stock`);
    }

    if (requestedQuantity > availableQuantity) {
      throw new Error(`Only ${availableQuantity} ${isService ? 'slots' : 'items'} available`);
    }

    // Always use the buyer's profile displayName (the name shown on their profile page)
    let buyerName = null;
    let buyerProfilePic = null;

    const buyerUserRef = doc(db, 'users', buyer.uid);
    const buyerUserSnap = await getDoc(buyerUserRef);
    if (buyerUserSnap.exists()) {
      const userData = buyerUserSnap.data();
      buyerProfilePic = userData.profilePic || null;
      if (!isGuest) {
        buyerName = userData.displayName || buyer.displayName || buyer.email;
      } else {
        buyerName = userData.guestInfo?.displayName || buyer.displayName || 'Guest';
        buyerProfilePic = buyerProfilePic || 'https://api.dicebear.com/7.x/avataaars/svg?seed=guest';
      }
    } else {
      buyerName = isGuest ? (buyer.displayName || 'Guest') : (buyer.displayName || buyer.email);
    }

    const costs = calculateTransactionCosts(unitPrice, requestedQuantity);

    // Normalise a Firestore profile field that can be a string URL or an object {image: "..."}
    const normProfile = (p) => (typeof p === 'string' ? p : p?.image || null);
    const sellerShopPic = normProfile(shopData.profile);

    // Fetch buyer's own shop pic so the seller tab can show it without a secondary read
    let buyerShopPic = null;
    try {
      const bsSnap = await getDoc(doc(db, 'shops', buyer.uid));
      if (bsSnap.exists()) buyerShopPic = normProfile(bsSnap.data().profile);
    } catch (_) {}

    const isDigital = item.isDigital || false;
    const jobType    = item.jobType    || null;
    const serviceTab = item.serviceTab || null;

    const transaction = {
      itemId,
      isService,
      isDigital,
      jobType,
      serviceTab,
      // Service-specific fields (rides / moving / delivery)
      rideDateTime:           item.rideDateTime           || null,
      pickupAddress:          item.pickupAddress          || null,
      pickupCoordinates:      item.pickupCoordinates      || null,
      destinationAddress:     item.destinationAddress     || null,
      destinationCoordinates: item.destinationCoordinates || null,
      passengers:             item.passengers             || null,
      estimatedDistance:      item.estimatedDistance      || null,
      vehicleType:            item.vehicleType            || null,
      serviceProgress:        null,
      useMockStripe: useMock,
      paymentMethod: useMock ? 'cash_on_pickup' : 'card',
      itemName: item.name,
      itemImage: item.images?.[0] || null,
      itemAddress: item.address || item.shopMasterAddresses?.[0]?.address || shopData.masterAddresses?.[0]?.address || null,
      itemCoordinates: item.coordinates || item.shopMasterAddresses?.[0]?.coordinates || shopData.masterAddresses?.[0]?.coordinates || null,
      unitPrice: parseFloat(unitPrice),
      requestedQuantity: parseInt(requestedQuantity),
      approvedQuantity: null,
      ...costs,
      sellerId,
      sellerName: shopData.name || '',
      sellerStripeAccountId: sellerStripeAccountId,
      sellerStripeStatus: sellerStripeStatus,
      currency: shopData.currency || 'USD',
      buyerId: buyer.uid,
      buyerName: buyerName,
      isGuest: isGuest,
      isSelfOrder: buyer.uid === sellerId,
      roles: {
        [buyer.uid]: 'buyer',
        [sellerId]: 'seller'
      },
      status: 'pending_seller_acceptance',
      paymentStatus: 'not_paid',
      meetupType: isDigital ? 'digital' : meetupType,
      transactionCode: null,
      customization: customization || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // ── Merge into existing open order from same seller (< 1 hr) ───────────
    // Merges whether the seller has responded yet or not — as long as payment
    // hasn't happened, new items just join the same chat/window. If the order
    // was already accepted, it stays accepted (items don't need re-approval).
    if (!isService && !isDigital) {
      const oneHourAgo = new Date(Date.now() - 3_600_000);
      const openSnap = await getDocs(query(
        collection(db, 'transactions'),
        where('buyerId',  '==', buyer.uid),
        where('sellerId', '==', sellerId),
        where('status',   'in', ['pending_seller_acceptance', 'seller_accepted']),
        limit(5)
      ));

      const recentDoc = openSnap.docs.find(d => {
        const created = d.data().createdAt?.toDate?.();
        return created && created >= oneHourAgo && !d.data().isService;
      });

      if (recentDoc) {
        const existingId   = recentDoc.id;
        const existingData = recentDoc.data();
        const chatId       = existingData.chatId || existingId;

        // Convert single-item flat fields → cartItems if needed
        let mergedItems = existingData.cartItems?.length > 0
          ? [...existingData.cartItems]
          : [{
              itemId:        existingData.itemId,
              itemName:      existingData.itemName,
              itemImage:     existingData.itemImage || null,
              unitPrice:     parseFloat(existingData.unitPrice) || 0,
              quantity:      existingData.requestedQuantity || 1,
              total:         parseFloat(((existingData.unitPrice || 0) * (existingData.requestedQuantity || 1)).toFixed(2)),
              customization: existingData.customization || null,
            }];

        const newQty = parseInt(requestedQuantity);
        const newPrice = parseFloat(unitPrice);
        const alreadyIn = mergedItems.find(ci => ci.itemId === itemId);
        if (alreadyIn) {
          mergedItems = mergedItems.map(ci =>
            ci.itemId === itemId
              ? { ...ci, quantity: ci.quantity + newQty, total: parseFloat((ci.unitPrice * (ci.quantity + newQty)).toFixed(2)) }
              : ci
          );
        } else {
          mergedItems.push({
            itemId,
            itemName:      item.name,
            itemImage:     item.images?.[0] || null,
            unitPrice:     newPrice,
            quantity:      newQty,
            total:         parseFloat((newPrice * newQty).toFixed(2)),
            customization: customization || null,
          });
        }

        const mergedSubtotal   = parseFloat(mergedItems.reduce((s, ci) => s + ci.unitPrice * ci.quantity, 0).toFixed(2));
        const mergedTotalFees  = mergedSubtotal * 0.07;
        const mergedStripeFee  = (mergedSubtotal * 0.029) + 0.30;
        const mergedPlatform   = parseFloat(Math.max(0, mergedTotalFees - mergedStripeFee).toFixed(2));
        const mergedPayout     = parseFloat((mergedSubtotal - mergedTotalFees).toFixed(2));
        const mergedCosts      = {
          subtotal: mergedSubtotal, total: mergedSubtotal,
          transactionFee: 0, salesTax: 0,
          platformFee: mergedPlatform,
          stripeFee:   parseFloat(mergedStripeFee.toFixed(2)),
          sellerPayout: mergedPayout,
        };

        await updateDoc(doc(db, 'transactions', existingId), {
          cartItems: mergedItems, isCartOrder: true, ...mergedCosts, updatedAt: serverTimestamp(),
        });
        await updateDoc(doc(db, 'chats', chatId), {
          cartItems: mergedItems, ...mergedCosts,
          lastMessage: `Added ${newQty}x ${item.name} to order`,
          lastMessageTime: serverTimestamp(),
          [`unreadCount.${sellerId}`]: increment(1),
        });
        await addDoc(collection(db, 'chats', chatId, 'messages'), {
          text: `Added ${newQty}x ${item.name} to the order.`,
          sender: buyer.uid,
          senderName: buyerName,
          timestamp: serverTimestamp(),
          type: 'item_added',
          purchaseData: { itemName: item.name, quantity: newQty, unitPrice: newPrice, ...mergedCosts },
        });

        return {
          transactionId: existingId, chatId, merged: true,
          status: existingData.status, costs: mergedCosts,
          useMockStripe: existingData.useMockStripe ?? useMock,
          sellerStripeConnected: !(existingData.useMockStripe ?? useMock),
          isService: false,
        };
      }
    }
    // ────────────────────────────────────────────────────────────────────────

    const transactionRef = await addDoc(collection(db, 'transactions'), transaction);
    const transactionId = transactionRef.id;
    // chatId === transactionId for single-item orders (chat doc uses transactionId as its key)
    await updateDoc(transactionRef, { chatId: transactionId });

    const orderNumber = transactionId.slice(-6).toUpperCase();

    const chatDoc = {
      transactionId,
      itemId: item.id,
      isService,
      itemName: item.name,
      itemImage: item.images?.[0] || null,
      buyerId: buyer.uid,
      buyerName: buyerName,
      sellerId,
      sellerName: shopData.name || '',
      currency: shopData.currency || 'USD',
      participants: [...new Set([buyer.uid, sellerId])],
      isSelfOrder: buyer.uid === sellerId,
      paymentMethod: useMock ? 'cash_on_pickup' : 'card',
      roles: {
        [buyer.uid]: 'buyer',
        [sellerId]: 'seller'
      },
      isGuest: isGuest,
      title: `Order #${orderNumber}`,
      chatImage: buyerProfilePic || 'https://api.dicebear.com/7.x/avataaars/svg?seed=default',
      sellerShopPic,
      buyerShopPic,
      lastMessage: `Hello, I'd like to order ${requestedQuantity} ${isService ? 'slot(s) for' : 'x'} ${item.name}. Is this available?`,
      lastMessageTime: serverTimestamp(),
      unreadCount: {
        [buyer.uid]: 0,
        [sellerId]: 1
      },
      pendingPurchase: {
        itemId,
        isService,
        itemName: item.name,
        unitPrice: parseFloat(unitPrice),
        requestedQuantity: parseInt(requestedQuantity),
        ...costs,
        status: 'pending_seller_acceptance',
        buyerName: buyerName,
        availableQuantity
      }
    };

    // ✅ Use transaction ID as chat ID
    await setDoc(doc(db, 'chats', transactionId), chatDoc);

    // Hidden system message (buyer_inquiry is filtered in ChatView)
    await addDoc(collection(db, 'chats', transactionId, 'messages'), {
      text: `Hello, I'd like to order ${requestedQuantity} ${isService ? 'slot(s) for' : 'x'} ${item.name}. Is this available?`,
      sender: buyer.uid,
      senderName: buyerName,
      timestamp: serverTimestamp(),
      type: 'buyer_inquiry',
      purchaseData: { itemName: item.name, isService, ...costs }
    });

    // Visible order details message — seller sees this as first message in chat
    const orderDetailsText = formatOrderDetailsMessage(customization?.orderDetails);
    const orderSummaryLines = [
      `📋 New ${isService ? 'Booking' : 'Order'} Request`,
      `Item: ${item.name}${unitPrice ? ` · $${parseFloat(unitPrice).toFixed(2)}${isService ? ' total' : ''}` : ''}`,
      `Qty: ${requestedQuantity}`,
      ...(orderDetailsText ? [orderDetailsText.trim()] : []),
    ].join('\n');
    await addDoc(collection(db, 'chats', transactionId, 'messages'), {
      text: orderSummaryLines,
      sender: buyer.uid,
      senderName: buyerName,
      timestamp: serverTimestamp(),
      type: 'order_summary',
    });

    const autoAcceptWillTrigger = shopData.autoAcceptOrders !== false && !isDigital;
    await NotificationService.notifyNewOrderRequest(
      sellerId,
      buyerName,
      [{ itemName: item.name, quantity: parseInt(requestedQuantity), unitPrice: parseFloat(unitPrice), customization: customization || null }],
      costs.total,
      transactionId,
      autoAcceptWillTrigger,
      shopData.name || ''
    );

    // ===== DIGITAL PRODUCT AUTO-ACCEPT =====
    if (isDigital) {
      try {
        const approvedQty = parseInt(requestedQuantity);
        const digitalCosts = calculateTransactionCosts(unitPrice, approvedQty);

        await updateDoc(transactionRef, {
          status: 'seller_accepted',
          approvedQuantity: approvedQty,
          ...digitalCosts,
          finalTotalPrice: digitalCosts.total,
          sellerResponseTime: serverTimestamp(),
          autoAccepted: true,
          updatedAt: serverTimestamp()
        });

        const chatDocRef = doc(db, 'chats', transactionId);
        await updateDoc(chatDocRef, {
          status: 'seller_accepted',
          transactionStatus: 'seller_accepted',
          lastMessage: `Digital product order confirmed — $${digitalCosts.total.toFixed(2)}`,
          lastMessageTime: serverTimestamp(),
          [`unreadCount.${buyer.uid}`]: 1,
          updatedAt: serverTimestamp()
        });

        await addDoc(collection(db, 'chats', transactionId, 'messages'), {
          text: `Digital product order confirmed! Complete payment to receive your instant download link.`,
          sender: 'system',
          senderName: 'System',
          timestamp: serverTimestamp(),
          type: 'order_accepted',
          autoAccepted: true
        });

        console.log('✅ Digital product auto-accepted');
      } catch (digitalAcceptError) {
        console.error('⚠️ Digital auto-accept failed (non-fatal):', digitalAcceptError);
      }
    }
    // ===== END DIGITAL AUTO-ACCEPT =====

    // ===== AUTO-ACCEPT CHECK =====
    try {
      const autoAcceptEnabled = shopData.autoAcceptOrders !== false;
      const isSelfOrderSingle = buyer.uid === sellerId;

      if (isSelfOrderSingle || (autoAcceptEnabled && !isDigital)) {
        console.log(isSelfOrderSingle ? '🔄 Self-order — auto-accepting' : ' Auto-accept enabled — accepting order immediately');

        const approvedQty = parseInt(requestedQuantity);
        const costs = calculateTransactionCosts(unitPrice, approvedQty);

        // Update transaction to seller_accepted
        await updateDoc(transactionRef, {
          status: 'seller_accepted',
          approvedQuantity: approvedQty,
          ...costs,
          finalTotalPrice: costs.total,
          sellerResponseTime: serverTimestamp(),
          autoAccepted: true,
          updatedAt: serverTimestamp()
        });

        // Update chat doc
        const chatDocRef = doc(db, 'chats', transactionId);
        await updateDoc(chatDocRef, {
          status: 'seller_accepted',
          transactionStatus: 'seller_accepted',
          lastMessage: `Order auto-accepted — $${costs.total.toFixed(2)}`,
          lastMessageTime: serverTimestamp(),
          [`unreadCount.${buyer.uid}`]: 1,
          updatedAt: serverTimestamp()
        });

        // System message
        await addDoc(collection(db, 'chats', transactionId, 'messages'), {
          text: isSelfOrderSingle
            ? `✅ Self-order confirmed!\n\nTotal: $${costs.total.toFixed(2)}\n\nPay now to complete your order instantly.`
            : ` Order auto-accepted!\n\nTotal: $${costs.total.toFixed(2)}\n\nProceed to payment when ready.`,
          sender: 'system',
          senderName: 'System',
          timestamp: serverTimestamp(),
          type: 'order_accepted',
          autoAccepted: true,
          isSelfOrder: isSelfOrderSingle,
        });

        console.log('✅ Auto-accept complete');
      }
    } catch (autoAcceptError) {
      console.error('⚠️ Auto-accept failed (non-fatal):', autoAcceptError);
      // Don't throw — order was created, just not auto-accepted
    }
    // ===== END AUTO-ACCEPT CHECK =====

    return {
      transactionId,
      status: 'pending_seller_acceptance',
      costs,
      useMockStripe: useMock,
      sellerStripeConnected: !useMock,
      isService
    };
  } catch (error) {
    console.error('Error initiating transaction:', error);
    throw error;
  }
},

  // Step 2: Seller accepts/rejects - FIXED with missing imports and logic
respondToQuantityRequest: async (transactionId, decision, approvedQuantityOrCart = null, sellerMessage = null) => {
  console.log('🎯 RESPOND TO QUANTITY REQUEST - START');
  console.log('📝 Input parameters:', { 
    transactionId, 
    decision, 
    approvedQuantityOrCart, 
    hasApprovedQty: !!approvedQuantityOrCart 
  });

  

  try {
    // 1. Get current user
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated');
    }
    
    console.log('👤 Current user:', { uid: currentUser.uid, email: currentUser.email });

    // 2. Get transaction document
    const transactionRef = doc(db, 'transactions', transactionId);
    const transactionDoc = await getDoc(transactionRef);
    
    if (!transactionDoc.exists()) {
      throw new Error('Transaction not found');
    }

    const transaction = { id: transactionDoc.id, ...transactionDoc.data() };
    console.log('✅ Transaction found:', { 
      id: transaction.id,
      sellerId: transaction.sellerId,
      buyerId: transaction.buyerId,
      status: transaction.status,
      itemName: transaction.itemName,
      isCartOrder: transaction.isCartOrder || false,
      cartItems: transaction.cartItems ? transaction.cartItems.length : 0
    });

    console.log('🔍 DEBUG - What is approvedQuantityOrCart?:', {
  type: typeof approvedQuantityOrCart,
  isArray: Array.isArray(approvedQuantityOrCart),
  value: approvedQuantityOrCart,
  stringified: JSON.stringify(approvedQuantityOrCart)
});

console.log('🔍 DEBUG - Transaction cart items:', {
  hasCartItems: !!transaction.cartItems,
  cartItems: transaction.cartItems,
  isCartOrder: transaction.isCartOrder
});

    // 3. Verify authorization - user must be the seller, a shared staff member,
    // or the owner of this sub-shop (ownerId on the shop doc, distinct from its own UID)
    if (currentUser.uid !== transaction.sellerId) {
      const shopCheckRef = doc(db, 'shops', transaction.sellerId);
      const shopCheckSnap = await getDoc(shopCheckRef);
      const shopCheckData = shopCheckSnap.exists() ? shopCheckSnap.data() : null;
      const isSharedMember = !!shopCheckData?.sharedUserMap?.[currentUser.uid];
      const isSubShopOwner = shopCheckData?.ownerId === currentUser.uid;

      if (!isSharedMember && !isSubShopOwner) {
        console.error('❌ Unauthorized:', {
          currentUserId: currentUser.uid,
          transactionSellerId: transaction.sellerId
        });
        throw new Error('Only the seller can respond to quantity requests');
      }
      console.log('✅ Shared/sub-shop member authorization verified');
    } else {
      console.log('✅ Seller authorization verified');
    }

    // 4. Validate transaction status
    if (transaction.status !== 'pending_seller_acceptance') {
      throw new Error(`Cannot respond - transaction is ${transaction.status}`);
    }

    let sellerStripeAccountId = null;
    let sellerStripeStatus = null;
    
    if (decision === 'accept') {
  // ⚠️ Stripe verification is disabled for now — all orders use the mock payment flow
  // regardless of the seller's Stripe connection status. See buildOptimisticChat/useMock.
  const shopRef = doc(db, 'shops', transaction.sellerId);
  const shopSnap = await getDoc(shopRef);

  if (!shopSnap.exists()) {
    console.error('❌ Seller shop document not found');
    throw new Error('Shop not found. Please set up your shop first.');
  }

  const shopData = shopSnap.data();
  sellerStripeAccountId = shopData.stripeAccountId || null;
  sellerStripeStatus = shopData.stripeAccountStatus || 'not_connected';
}

    // 5. Determine order type
    const isCartOrder = transaction.isCartOrder || 
                       (transaction.cartItems && transaction.cartItems.length > 0);
    
    console.log('🛒 Order type:', isCartOrder ? 'Cart Order' : 'Single Item Order');

    let totalAmount = 0;
    let cartItems = [];
    let approvedQuantity = null;

    if (decision === 'accept') {
  if (isCartOrder) {
    // Handle cart order acceptance
    console.log('📦 Processing cart order acceptance');
    
    // Get cart items from transaction if not provided
    if (!approvedQuantityOrCart || !Array.isArray(approvedQuantityOrCart)) {
      console.warn('⚠️ No cart items provided, using from transaction');
      
      if (!transaction.cartItems || !Array.isArray(transaction.cartItems)) {
        throw new Error('Cart items not found in transaction');
      }
      
      cartItems = transaction.cartItems;
    } else {
      cartItems = approvedQuantityOrCart;
    }
    
    console.log('🛒 Raw cart items to process:', cartItems);
    
    // Validate and format cart items
    let subtotal = 0;
    const validatedCartItems = [];
    
    for (const cartItem of cartItems) {
      console.log('🔍 Processing cart item:', cartItem);
      
      try {
        // Extract fields with flexible property names
        const itemId = cartItem.itemId || cartItem.id;
        const itemName = cartItem.itemName || cartItem.name || 'Unknown Item';
        const unitPrice = parseFloat(
          cartItem.unitPrice || 
          cartItem.price || 
          cartItem.unit_price || 
          0
        );
        const quantity = parseInt(
          cartItem.quantity || 
          cartItem.qty || 
          cartItem.requestedQuantity || 
          1
        );
        
        console.log('📊 Extracted fields:', { itemId, itemName, unitPrice, quantity });
        
        if (!itemId) {
          console.error('❌ Missing itemId:', cartItem);
          throw new Error(`Cart item missing ID: ${JSON.stringify(cartItem)}`);
        }
        
        if (unitPrice <= 0) {
          console.error('❌ Invalid unitPrice:', unitPrice);
          throw new Error(`Invalid price for item ${itemName}: $${unitPrice}`);
        }
        
        if (quantity <= 0) {
          console.error('❌ Invalid quantity:', quantity);
          throw new Error(`Invalid quantity for item ${itemName}: ${quantity}`);
        }
        
        const itemTotal = unitPrice * quantity;
        subtotal += itemTotal;
        
        validatedCartItems.push({
          itemId,
          itemName,
          itemImage: cartItem.itemImage || cartItem.image || cartItem.itemImageUrl || null,
          unitPrice,
          quantity,
          total: parseFloat(itemTotal.toFixed(2)),
          customization: cartItem.customization || null
        });
        
        console.log('✅ Item processed successfully');
        
      } catch (itemError) {
        console.error('❌ Error processing cart item:', itemError.message);
        throw new Error(`Failed to process cart item: ${itemError.message}`);
      }
    }
    
    if (validatedCartItems.length === 0) {
      throw new Error('No valid cart items found');
    }
    
    // Calculate fees and taxes
    totalAmount = subtotal;
    
    cartItems = validatedCartItems;
    
    

  } else {
    // Handle single item acceptance
    console.log('📦 Processing single item acceptance');
    
    if (!approvedQuantityOrCart || typeof approvedQuantityOrCart !== 'number') {
      throw new Error('For single items, approvedQuantity must be a number');
    }

    approvedQuantity = Math.max(1, Math.min(
      approvedQuantityOrCart, 
      transaction.requestedQuantity || 1
    ));

    // Calculate totals
    const subtotal = transaction.unitPrice * approvedQuantity;
    totalAmount = subtotal;
    
  }
}

    // 6. Update transaction document
    console.log('🔍 PRE-UPDATE VALIDATION:', {
      currentUserId: currentUser.uid,
      transactionSellerId: transaction.sellerId,
      transactionBuyerId: transaction.buyerId,
      matches: currentUser.uid === transaction.sellerId,
      isCartOrder,
      hasRequiredFields: !isCartOrder || (cartItems && cartItems.length > 0)
    });

    if (!isCartOrder && decision === 'accept') {
      if (!transaction.unitPrice || !approvedQuantity) {
        throw new Error('Missing required fields for single item order');
      }
    }

    console.log('📝 Updating transaction document...');
    
    const updateData = {
  status: decision === 'accept' ? 'seller_accepted' : 'seller_rejected',
  updatedAt: serverTimestamp(),
  sellerResponseTime: serverTimestamp(),
  // ✅ KEEP the original Stripe fields - don't overwrite:
  buyerId: transaction.buyerId,
  sellerId: transaction.sellerId,
  // DO NOT add sellerStripeAccountId or sellerStripeStatus here
  // They should already be in the transaction from creation
  ...(sellerMessage && { sellerMessage })
};

    if (decision === 'accept') {
      if (isCartOrder) {
  updateData.cartItems = cartItems;
  updateData.subtotal = cartItems.reduce((sum, item) => sum + item.total, 0);
  updateData.transactionFee = 0;
  updateData.salesTax = 0;
  updateData.total = updateData.subtotal; // Customer pays only subtotal
  updateData.approvedQuantity = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  // Calculate seller payout (93% of total)
  const totalFees = updateData.total * 0.07;
  updateData.platformFee = Math.max(0, totalFees - ((updateData.total * 0.029) + 0.30));
  updateData.stripeFee = (updateData.total * 0.029) + 0.30;
  updateData.sellerPayout = updateData.total - totalFees;
} else {
  updateData.approvedQuantity = approvedQuantity;
  updateData.subtotal = transaction.unitPrice * approvedQuantity;
  updateData.transactionFee = 0;
  updateData.salesTax = 0;
  updateData.total = updateData.subtotal; // Customer pays only subtotal
  // Calculate seller payout (93% of total)
  const totalFees = updateData.total * 0.07;
  updateData.platformFee = Math.max(0, totalFees - ((updateData.total * 0.029) + 0.30));
  updateData.stripeFee = (updateData.total * 0.029) + 0.30;
  updateData.sellerPayout = updateData.total - totalFees;
}
      updateData.finalTotalPrice = updateData.total;
    }

    console.log('📦 Update data:', updateData);
    
    await updateDoc(transactionRef, updateData);
    console.log('✅ Transaction updated successfully');

    // 7. Find and update chat document
    console.log('📝 Updating chat document...');
    
    let chatId = transaction.chatId;
    
    if (!chatId) {
  console.error('❌ CRITICAL: No chatId found in transaction!');
  console.error('Transaction ID:', transactionId);
  console.error('Transaction data:', transaction);
  throw new Error('Transaction is missing chatId field - cannot update chat');
}

console.log('✅ Found chatId in transaction:', chatId);

try {
  const chatRef = doc(db, 'chats', chatId);
  const chatSnap = await getDoc(chatRef);
  
  if (!chatSnap.exists()) {
    throw new Error(`Chat document ${chatId} not found`);
  }
  
  const chatData = chatSnap.data();
  console.log('📋 Current chat data:', {
    id: chatSnap.id,
    buyerId: chatData.buyerId,
    sellerId: chatData.sellerId,
    status: chatData.status
  });
  
  // ✅ FIXED: Only update fields that don't violate security rules
  const chatUpdateData = {
    status: decision === 'accept' ? 'seller_accepted' : 'seller_rejected',
    transactionStatus: decision === 'accept' ? 'seller_accepted' : 'seller_rejected',
    lastMessage: decision === 'accept' 
      ? `Seller accepted your order - $${totalAmount.toFixed(2)}` 
      : 'Seller rejected your order',
    lastMessageTime: serverTimestamp(),
    [`unreadCount.${transaction.buyerId}`]: 1,
    updatedAt: serverTimestamp()
  };
  
  console.log('📤 Updating chat with:', chatUpdateData);
  
  await updateDoc(chatRef, chatUpdateData);
  console.log('✅ Chat document updated');
  
} catch (chatError) {
  console.error('❌ Chat update failed:', chatError.message);
  console.error('❌ Chat error code:', chatError.code);
  
  if (chatError.code === 'permission-denied') {
    console.error('🔒 PERMISSION DENIED DETAILS:');
    console.error('   Current user:', currentUser.uid);
    console.error('   Transaction seller:', transaction.sellerId);
    console.error('   Transaction buyer:', transaction.buyerId);
    console.error('   Chat ID:', chatId);
  }
  
  throw chatError; // Throw so we know chat update failed
}
    

    // 8. Add message to chat
    console.log('📨 Adding acceptance message to chat...');
    
    if (chatId) {
      try {
        const messageText = decision === 'accept'
          ? (isCartOrder 
            ? `✅ Seller accepted your cart order! Total: $${totalAmount.toFixed(2)}` 
            : `✅ Seller accepted ${approvedQuantity}x ${transaction.itemName}! Total: $${totalAmount.toFixed(2)}`)
          : '❌ Order is not available';
        
        await addDoc(collection(db, 'chats', chatId, 'messages'), {
          text: messageText,
          sender: 'system',
          senderName: 'System',
          timestamp: serverTimestamp(),
          type: decision === 'accept' ? 'order_accepted' : 'order_rejected',
          transactionId: transactionId,
          ...(decision === 'accept' && isCartOrder && { cartItems }),
          ...(decision === 'accept' && !isCartOrder && { approvedQuantity })
        });
        
        console.log('✅ Acceptance message added to chat');
        
      } catch (messageError) {
        console.error('❌ Failed to add message:', messageError.message);
        
        // Try API fallback for message
        
      }
    }

    // 9. Send notification to buyer
    console.log('🔔 Sending notification to buyer...');
    
    try {
      const notifItemName = isCartOrder
        ? `${cartItems.length} item${cartItems.length !== 1 ? 's' : ''}`
        : (transaction.itemName || 'Your order');
      const notifQty = isCartOrder
        ? cartItems.reduce((s, ci) => s + (ci.quantity || 1), 0)
        : (transaction.approvedQuantity || transaction.requestedQuantity || 1);

      if (decision === 'accept') {
        await NotificationService.notifyOrderAccepted(
          transaction.buyerId,
          transaction.sellerName || '',
          notifItemName,
          notifQty,
          totalAmount || transaction.total || 0,
          transactionId
        );
      } else {
        await NotificationService.notifyOrderRejected(
          transaction.buyerId,
          transaction.sellerName || '',
          notifItemName,
          transactionId
        );
      }
      console.log('✅ Notification sent');
    } catch (notificationError) {
      console.error('❌ Notification error:', notificationError.message);
    }

    console.log(`🎯 ${isCartOrder ? 'CART' : 'ITEM'} ORDER ${decision.toUpperCase()} - SUCCESS`);
    
    return {
      success: true,
      transactionId,
      chatId,
      decision,
      totalAmount: decision === 'accept' ? totalAmount : null,
      ...(isCartOrder && { cartItems }),
      ...(!isCartOrder && decision === 'accept' && { approvedQuantity })
    };

  } catch (error) {
    console.error('❌ Error in respondToQuantityRequest:', error);
    throw error;
  }
},

  addToCart: async (itemId, sellerId, unitPrice, requestedQuantity, meetupType = 'inperson', isService = false, customization = null) => {
  console.log('═══════════════════════════════════════');
  console.log('🛒 ADD TO CART - START');
  console.log('═══════════════════════════════════════');
  
  try {
    // ✅ SIMPLIFIED: Check authentication (works for both regular and anonymous)
    console.log('📍 Step 1: Checking authentication...');
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      console.error('❌ No authenticated user found');
      throw new Error('You must be logged in to add items to cart');
    }

    console.log('   Auth state:', {
      isAuthenticated: true,
      uid: currentUser.uid,
      isAnonymous: currentUser.isAnonymous,
      displayName: currentUser.displayName
    });

    // ✅ Step 2: Verify auth token (works for anonymous too)
    console.log('📍 Step 2: Verifying auth token...');
    try {
      const token = await currentUser.getIdToken(true);
      console.log('   ✅ Token obtained');
      
      const tokenResult = await currentUser.getIdTokenResult();
      console.log('   Token claims:', {
        uid: tokenResult.claims.user_id || tokenResult.claims.sub,
        isAnonymous: currentUser.isAnonymous
      });
    } catch (tokenError) {
      console.error('❌ Auth token error:', tokenError);
      throw new Error('Authentication error - please sign in again');
    }

    // Step 3: Validate inputs
    console.log('📍 Step 3: Validating inputs...');
    console.log('   Input parameters:', {
      itemId,
      sellerId,
      unitPrice,
      requestedQuantity,
      meetupType,
      isService
    });

    // Step 4: Get shop data
console.log('📍 Step 4: Fetching shop data...');
const shopRef = doc(db, 'shops', sellerId);
const shopSnap = await getDoc(shopRef);

if (!shopSnap.exists()) {
  throw new Error('Shop not found');
}

const shopData = shopSnap.data();

// Step 5: Find item - CHECK BOTH items AND services arrays
console.log('📍 Step 5: Finding item/service/event...');
console.log('   Looking for:', {
  itemId,
  isService,
  itemsCount: shopData.items?.length || 0,
  servicesCount: shopData.services?.length || 0
});

// First, try the expected array based on isService flag
let item = null;
let itemType = null;

if (isService) {
  item = shopData.services?.find(s => s.id === itemId);
  if (item) {
    itemType = 'service';
    console.log('✅ Found in services array');
  }
}

// If not found, try items array
if (!item) {
  item = shopData.items?.find(i => i.id === itemId);
  if (item) {
    itemType = 'item';
    isService = false; // ✅ Correct the flag
    console.log('✅ Found in items array');
  }
}

// If still not found, try services array (fallback)
if (!item && !isService) {
  item = shopData.services?.find(s => s.id === itemId);
  if (item) {
    itemType = 'service';
    isService = true; // ✅ Correct the flag
    console.log('✅ Found in services array (fallback)');
  }
}

// ✅ SHOULD BE:
if (!item) {
  console.log('   Checking community posts for events...');
  console.log('   Event ID to find:', itemId);
  
  try {
    // Events are stored as documents in communityPosts subcollection
    const eventDocRef = doc(db, 'shops', sellerId, 'communityPosts', itemId);
    const eventSnap = await getDoc(eventDocRef);
    
    console.log('   Event document exists:', eventSnap.exists());
    
    if (eventSnap.exists()) {
      const eventData = eventSnap.data();
      console.log('   Event data:', {
        type: eventData.type,
        isEvent: eventData.isEvent,
        eventTitle: eventData.eventTitle,
        price: eventData.price,
        ticketPrice: eventData.ticketPrice,
        slots: eventData.slots,
        capacity: eventData.capacity
      });
      
      // ✅ Check if this is actually an event post
      if (eventData.type === 'event' || eventData.isEvent === true) {
        item = {
          id: eventSnap.id,  // ✅ Use document ID
          name: eventData.eventTitle || eventData.title || 'Event',
          price: eventData.ticketPrice || eventData.price || 0,
          images: eventData.images || (eventData.image ? [eventData.image] : []),
          quantity: eventData.slots || eventData.capacity || 1,
          isEvent: true,
          address: eventData.location || eventData.address || null,
          coordinates: eventData.coordinates || null,
          eventDate: eventData.eventDate || null,
          eventTime: eventData.eventTime || null
        };
        itemType = 'event';
        isService = true; // Treat events like services (use slots)
        console.log('✅ Found as event in community posts');
      } else {
        console.log('   Document exists but is not an event:', eventData.type);
      }
    } else {
      console.log('   Event document does not exist at path:', eventDocRef.path);
    }
  } catch (eventError) {
    console.error('   Error fetching event:', eventError);
  }
}

if (!item) {
  console.error('❌ Item/Service/Event not found:', {
    searchedId: itemId,
    isService,
    checkedItems: shopData.items?.length || 0,
    checkedServices: shopData.services?.length || 0
  });
  throw new Error(`${isService ? 'Service/Event' : 'Item'} not found`);
}

console.log('✅ Found:', {
  id: item.id,
  name: item.name,
  type: itemType,
  isService,
  hasSlots: item.hasOwnProperty('slots'),
  hasQuantity: item.hasOwnProperty('quantity')
});

    // Step 6: Check for existing cart (products only, ≤ 24 hrs old)
    console.log('📍 Step 6: Checking for existing cart...');

    // Services always create a fresh transaction — never reuse a cart
    if (isService) {
      console.log('   ℹ️ Service item — skipping existing-cart check, creating new transaction');
      // Fall through to the "create new cart" path below
      return await TransactionService.initiateQuantityTransaction(
        itemId, sellerId, unitPrice, requestedQuantity, meetupType || 'inperson', true, customization
      );
    }

    const chatsRef = collection(db, 'chats');
    console.log('   Chats collection ref:', chatsRef.path);

    const cartQuery = query(
      chatsRef,
      where('buyerId', '==', currentUser.uid),
      where('sellerId', '==', sellerId),
      where('status', '==', 'pending_send'),
      where('isCart', '==', true)
    );
    
    console.log('   Query parameters:', {
      buyerId: currentUser.uid,
      sellerId: sellerId,
      status: 'pending_send',
      isCart: true
    });

    let cartSnapshot;
    try {
      cartSnapshot = await getDocs(cartQuery);
      console.log('   Query result:', {
        empty: cartSnapshot.empty,
        size: cartSnapshot.size,
        docs: cartSnapshot.docs.length
      });
    } catch (queryError) {
      console.error('❌ Query error:', {
        code: queryError.code,
        message: queryError.message,
        stack: queryError.stack
      });
      throw queryError;
    }

    if (!cartSnapshot.empty) {
      // Only reuse the cart if it was updated within the last 24 hours
      const existingCartDoc = cartSnapshot.docs[0];
      const existingCartData = existingCartDoc.data();
      const cartUpdatedAt = existingCartData.lastMessageTime?.toDate?.() || existingCartData.updatedAt?.toDate?.() || null;
      const cartAgeMs = cartUpdatedAt ? Date.now() - cartUpdatedAt.getTime() : Infinity;

      if (cartAgeMs <= 86_400_000) {
      // UPDATE EXISTING CART
      console.log('═══════════════════════════════════════');
      console.log('📝 UPDATING EXISTING CART');
      console.log('═══════════════════════════════════════');

      const cartId = existingCartDoc.id;

      console.log('   Existing cart:', {
        id: cartId,
        currentItems: existingCartData.cartItems?.length || 0,
        currentTotal: existingCartData.total
      });

      const existingItem = existingCartData.cartItems?.find(ci => ci.itemId === itemId);
      console.log('   Item already in cart:', !!existingItem);

      let updatedCartItems;
      if (existingItem) {
        console.log('   Updating item quantity:', {
          current: existingItem.quantity,
          adding: requestedQuantity,
          new: existingItem.quantity + requestedQuantity
        });
        
        updatedCartItems = existingCartData.cartItems.map(ci =>
          ci.itemId === itemId
            ? { ...ci, quantity: ci.quantity + requestedQuantity }
            : ci
        );
      } else {
        console.log('   Adding new item to cart');
        const newCartItem = {
          itemId,
          itemName: item.name,
          itemImage: item.images?.[0] || null,
          unitPrice: parseFloat(unitPrice),
          quantity: requestedQuantity
        };
        updatedCartItems = [...(existingCartData.cartItems || []), newCartItem];
      }

      const newTotals = TransactionService.calculateCartTotal(updatedCartItems);
      console.log('   New totals:', newTotals);

      try {
        const cartRef = doc(db, 'chats', cartId);
        console.log('   Updating document:', cartRef.path);
        
        await updateDoc(cartRef, {
          cartItems: updatedCartItems,
          ...newTotals,
          lastMessageTime: serverTimestamp()
        });

        console.log('✅ Cart updated successfully');
        console.log('═══════════════════════════════════════\n');

        return {
          success: true,
          transactionId: cartId,
          isNewCart: false
        };
      } catch (updateError) {
        console.error('❌ Update error:', {
          code: updateError.code,
          message: updateError.message,
          stack: updateError.stack
        });
        throw updateError;
      }
      } // close if (cartAgeMs <= 86_400_000)
    } // close if (!cartSnapshot.empty)

    // CREATE NEW CART
      console.log('═══════════════════════════════════════');
      console.log('🆕 CREATING NEW CART');
      console.log('═══════════════════════════════════════');

      const buyerRef = doc(db, 'users', currentUser.uid);
      const buyerSnap = await getDoc(buyerRef);
      const buyerUserData = buyerSnap.exists() ? buyerSnap.data() : null;
      const buyerProfilePic = buyerUserData?.profilePic || null;
      const buyerDisplayName = buyerUserData?.displayName || currentUser.displayName || currentUser.email || 'Anonymous';
      const _normProfile = (p) => (typeof p === 'string' ? p : p?.image || null);
      const cartSellerShopPic = _normProfile(shopData.profile);
      let cartBuyerShopPic = null;
      try {
        const cbsSnap = await getDoc(doc(db, 'shops', currentUser.uid));
        if (cbsSnap.exists()) cartBuyerShopPic = _normProfile(cbsSnap.data().profile);
      } catch (_) {}


      const cartItem = {
        itemId,
        itemName: item.name,
        itemImage: item.images?.[0] || null,
        unitPrice: parseFloat(unitPrice),
        quantity: requestedQuantity,
        isService: isService || false,
        isEvent: item.isEvent || false,
        itemType: itemType || 'item',
        customization: customization || null
      };
      console.log('   Cart item:', cartItem);

      // ✅ FIXED: Use the function directly
      const costs = TransactionService.calculateCartTotal([cartItem]);
      console.log('   Calculated costs:', costs);

      const chatData = {
  buyerId: currentUser.uid,
  buyerName: buyerDisplayName,
  buyerEmail: currentUser.email || null,
  sellerId: sellerId,
  sellerName: shopData.name || '',
  sellerStripeAccountId: shopData.stripeAccountId || null,
  sellerStripeStatus: shopData.stripeAccountStatus || 'not_connected',
  participants: [...new Set([currentUser.uid, sellerId])],
  isSelfOrder: currentUser.uid === sellerId,
  roles: {
    [currentUser.uid]: 'buyer',
    [sellerId]: 'seller'
  },
  status: 'pending_send',
  isCart: true,
  cartItems: [cartItem],
  ...costs,
  
  title: 'Shopping Cart',
  chatImage: buyerProfilePic || 'https://api.dicebear.com/7.x/avataaars/svg?seed=default',
  sellerShopPic: cartSellerShopPic,
  buyerShopPic: cartBuyerShopPic,
  
  lastMessage: 'Cart created - items pending',
  lastMessageTime: serverTimestamp(),
  unreadCount: {
    [currentUser.uid]: 0,
    [sellerId]: 0
  },
  meetupType: meetupType || 'inperson',
  customization: customization || null,
  createdAt: serverTimestamp()
};

      console.log('   Chat document to create:', {
        buyerId: chatData.buyerId,
        buyerName: chatData.buyerName,
        sellerId: chatData.sellerId,
        sellerName: chatData.sellerName,
        participants: chatData.participants,
        status: chatData.status,
        isCart: chatData.isCart,
        cartItemsCount: chatData.cartItems.length,
        total: chatData.total
      });

      console.log('📍 Step 7: Creating Firestore document...');
      try {
        const chatsCollection = collection(db, 'chats');
        console.log('   Collection path:', chatsCollection.path);
        console.log('   Collection type:', chatsCollection.type);
        
        console.log('   Attempting addDoc...');
        const chatRef = await addDoc(chatsCollection, chatData);
        const chatId = chatRef.id;

        console.log('✅ Document created successfully!');
        console.log('   Document ID:', chatId);
        console.log('   Document path:', chatRef.path);
        console.log('═══════════════════════════════════════\n');

        return {
          success: true,
          transactionId: chatId,
          isNewCart: true
        };
        
      } catch (firestoreError) {
        console.error('═══════════════════════════════════════');
        console.error('❌ FIRESTORE ERROR - DETAILED INFO');
        console.error('═══════════════════════════════════════');
        console.error('   Error code:', firestoreError.code);
        console.error('   Error message:', firestoreError.message);
        console.error('   Error name:', firestoreError.name);
        console.error('   Full error:', firestoreError);
        console.error('   Stack trace:', firestoreError.stack);
        
        // Check if it's a permission error
        if (firestoreError.code === 'permission-denied') {
          console.error('   🔒 PERMISSION DENIED - Debugging info:');
          console.error('      Current user UID:', currentUser.uid);
          console.error('      Participants array:', chatData.participants);
          console.error('      BuyerId:', chatData.buyerId);
          console.error('      Status:', chatData.status);
          console.error('      IsCart:', chatData.isCart);
          console.error('      UID in participants?:', chatData.participants.includes(currentUser.uid));
          console.error('      BuyerId matches UID?:', chatData.buyerId === currentUser.uid);
        }
        
        console.error('═══════════════════════════════════════\n');
        throw firestoreError;
      }

  } catch (error) {
    console.error('═══════════════════════════════════════');
    console.error('❌ ADD TO CART - FAILED');
    console.error('═══════════════════════════════════════');
    console.error('   Error type:', error.constructor.name);
    console.error('   Error message:', error.message);
    console.error('   Error code:', error.code);
    console.error('   Full error:', error);
    console.error('═══════════════════════════════════════\n');
    throw error;
  }
},
  
  // ADD: Send cart to seller
  sendCart: async (chatId, userType = 'authenticated', guestInfo = null, paymentMethod = 'card') => {
  try {
    console.log('📤 SEND CART - START');
    console.log('👤 User type:', userType);
    
    let buyerId = null;
    let buyerName = null;
    
    if (userType === 'authenticated') {
      const buyer = auth.currentUser;
      if (!buyer) throw new Error('Authentication required');
      buyerId = buyer.uid;
      // Always use Firestore profile displayName as the buyer name
      const buyerSnap = await getDoc(doc(db, 'users', buyer.uid));
      buyerName = (buyerSnap.exists() ? buyerSnap.data().displayName : null)
        || buyer.displayName
        || buyer.email;
      console.log('✅ Authenticated buyer:', { buyerId, buyerName });

    } else if (userType === 'guest') {
      // ✅ UPDATED: Get from current user (who is now anonymous)
      const buyer = auth.currentUser;

      if (!buyer || !buyer.isAnonymous) {
        throw new Error('Guest account not found. Please create account first.');
      }

      buyerId = buyer.uid;  // ✅ Firebase anonymous UID
      const guestSnap = await getDoc(doc(db, 'users', buyer.uid));
      buyerName = (guestSnap.exists() ? guestSnap.data().guestInfo?.displayName : null)
        || buyer.displayName
        || guestInfo?.displayName
        || 'Guest';
      
      console.log('🎭 Guest buyer (anonymous auth):', { 
        buyerId, 
        buyerName,
        isAnonymous: buyer.isAnonymous
      });
    } else {
      throw new Error('Invalid user type');
    }

    // ✅ Validate buyerId
    if (!buyerId) {
      throw new Error('Buyer ID is missing');
    }

    // Get chat data
    const chatRef = doc(db, 'chats', chatId);
    const chatSnap = await getDoc(chatRef);
    
    if (!chatSnap.exists()) {
      throw new Error('Cart not found');
    }
    
    const cartData = chatSnap.data();
    
    console.log('📦 Cart data:', {
      chatId,
      storedBuyerId: cartData.buyerId,
      currentBuyerId: buyerId,
      userType,
      status: cartData.status
    });
    
    // ✅ Verify ownership (simple UID check now)
    if (cartData.buyerId !== buyerId) {
      console.error('❌ Buyer ID mismatch:', {
        stored: cartData.buyerId,
        current: buyerId
      });
      throw new Error('This cart does not belong to you');
    }
    
    console.log('✅ Ownership verified');
    
    if (cartData.status !== 'pending_send') {
      throw new Error('Cart already sent');
    }
    
    if (!cartData.cartItems || cartData.cartItems.length === 0) {
      throw new Error('Cart is empty');
    }

    // Create transaction
    const transaction = {
      cartItems: cartData.cartItems,
      ...cartData,
      buyerId: buyerId,  // ✅ Firebase UID (works for both)
      buyerName: buyerName,
      sellerStripeAccountId: cartData.sellerStripeAccountId || null,
      sellerStripeStatus: cartData.sellerStripeStatus || 'not_connected',
      status: 'pending_seller_acceptance',
      paymentStatus: 'not_paid',
      meetupType: 'inperson',
      transactionCode: null,
      isGuestOrder: userType === 'guest',  // ✅ Track if guest
      isAnonymous: userType === 'guest',   // ✅ Track if anonymous
      guestInfo: userType === 'guest' ? {
        guestId: buyerId,  // ✅ Firebase UID
        displayName: buyerName,
        phoneNumber: guestInfo?.phoneNumber || 'N/A'
      } : null,
      paymentMethod: paymentMethod || 'card',
      requiresPickupPayment: paymentMethod === 'cash_on_pickup' || paymentMethod === 'card_on_pickup',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    console.log('📄 Creating transaction');
    const transactionRef = await addDoc(collection(db, 'transactions'), transaction);
    const transactionId = transactionRef.id;

    // ✅ Store chatId in transaction
    await updateDoc(transactionRef, {
      chatId: chatId
    });
    console.log('✅ Stored chatId in transaction:', chatId);
    
    // Update chat
    const orderNumber = transactionId.slice(-6).toUpperCase();

    await updateDoc(chatRef, {
      transactionId: transactionId,
      status: 'pending_seller_acceptance',
      isCart: false,
      title: `Order #${orderNumber}`,
      lastMessage: `Order request sent for ${cartData.cartItems.length} item(s)`,
      lastMessageTime: serverTimestamp(),
      [`unreadCount.${cartData.sellerId}`]: 1
    });
    
    // Add message
    const itemSummary = cartData.cartItems.map(ci => {
      let line = `${ci.quantity}x ${ci.itemName}`;
      if (ci.customization?.option) line += ` (${ci.customization.option})`;
      if (ci.customization?.instructions) line += `\n   📝 ${ci.customization.instructions}`;
      return line;
    }).join('\n');

    await addDoc(collection(db, 'chats', chatId, 'messages'), {
      text: `Hello, ${buyerName} would like to order:\n\n${itemSummary}\n\nTotal: $${cartData.total.toFixed(2)}`,
      sender: buyerId,
      senderName: buyerName,
      timestamp: serverTimestamp(),
      type: 'buyer_inquiry'
    });
    
    // ===== NOTIFY SELLER + AUTO-ACCEPT FOR CART ORDERS =====
    try {
      const isSelfOrderCart = buyerId === cartData.sellerId;
      const shopRef = doc(db, 'shops', cartData.sellerId);
      const shopSnap = await getDoc(shopRef);
      const shopData = shopSnap.exists() ? shopSnap.data() : {};
      const cartAutoAccepted = isSelfOrderCart || shopData.autoAcceptOrders !== false;

      // Notify seller with full item details (skip for self-orders)
      if (!isSelfOrderCart) {
        const notifCartItems = cartData.cartItems.map(ci => ({
          itemName: ci.itemName,
          quantity: ci.quantity,
          unitPrice: ci.unitPrice,
          customization: ci.customization || null
        }));
        await NotificationService.notifyNewOrderRequest(
          cartData.sellerId,
          buyerName,
          notifCartItems,
          cartData.subtotal || cartData.total || 0,
          transactionId,
          cartAutoAccepted,
          shopData.name || ''
        );
      }

      if (isSelfOrderCart || shopData.autoAcceptOrders !== false) {
          console.log(isSelfOrderCart ? '🔄 Self-order — auto-accepting' : 'Auto-accepting cart order');

          const subtotal = cartData.cartItems.reduce(
            (sum, item) => sum + item.unitPrice * item.quantity, 0
          );
          const totalFees = subtotal * 0.07;
          const stripeFee = subtotal * 0.029 + 0.30;
          const platformFee = Math.max(0, totalFees - stripeFee);
          const sellerPayout = subtotal - totalFees;
          const approvedQty = cartData.cartItems.reduce(
            (sum, item) => sum + item.quantity, 0
          );

          await updateDoc(transactionRef, {
            status: 'seller_accepted',
            approvedQuantity: approvedQty,
            subtotal: parseFloat(subtotal.toFixed(2)),
            transactionFee: 0,
            salesTax: 0,
            total: parseFloat(subtotal.toFixed(2)),
            platformFee: parseFloat(platformFee.toFixed(2)),
            stripeFee: parseFloat(stripeFee.toFixed(2)),
            sellerPayout: parseFloat(sellerPayout.toFixed(2)),
            finalTotalPrice: parseFloat(subtotal.toFixed(2)),
            sellerResponseTime: serverTimestamp(),
            autoAccepted: true,
            isSelfOrder: isSelfOrderCart,
            updatedAt: serverTimestamp()
          });

          await updateDoc(chatRef, {
            status: 'seller_accepted',
            transactionStatus: 'seller_accepted',
            isSelfOrder: isSelfOrderCart,
            lastMessage: isSelfOrderCart
              ? `Self-order confirmed — $${subtotal.toFixed(2)} — proceed to payment`
              : `Cart auto-accepted — $${subtotal.toFixed(2)}`,
            lastMessageTime: serverTimestamp(),
            [`unreadCount.${buyerId}`]: 1
          });

          await addDoc(collection(db, 'chats', chatId, 'messages'), {
            text: isSelfOrderCart
              ? `✅ Self-order confirmed!\n\nTotal: $${subtotal.toFixed(2)}\n\nPay now to complete your order instantly.`
              : ` Cart order auto-accepted!\n\nTotal: $${subtotal.toFixed(2)}\n\nProceed to payment when ready.`,
            sender: 'system',
            senderName: 'System',
            timestamp: serverTimestamp(),
            type: 'order_accepted',
            autoAccepted: true,
            isSelfOrder: isSelfOrderCart,
          });
        }
    } catch (autoCartErr) {
      console.error('⚠️ Cart auto-accept failed (non-fatal):', autoCartErr);
    }
    // ===== END AUTO-ACCEPT CART =====
    
    console.log('✅ Cart sent successfully!');
    return {
      success: true,
      transactionId: transactionId,
      chatUpdated: true
    };
  } catch (error) {
    console.error('❌ Error sending cart:', error);
    throw error;
  }
},

  processQuantityPayment: async (transactionId, stripePaymentMethodId) => {
  try {
    console.log('💰 Processing payment for:', transactionId);
    
    const buyer = auth.currentUser;
    if (!buyer) {
      throw new Error('Authentication required');
    }

    const buyerId = buyer.uid;
    const buyerName = buyer.displayName || buyer.email || 'Customer';
    
    console.log('👤 Buyer:', { buyerId, buyerName, isAnonymous: buyer.isAnonymous });

    // Get transaction
    const transactionRef = doc(db, 'transactions', transactionId);
    const transactionSnap = await getDoc(transactionRef);
    
    if (!transactionSnap.exists()) {
      throw new Error('Transaction not found');
    }
    
    const transaction = transactionSnap.data();
    
    // Verify ownership
    if (transaction.buyerId !== buyerId) {
      throw new Error('This transaction does not belong to you');
    }

    if (transaction.status !== 'seller_accepted') {
      throw new Error('Transaction must be accepted by seller before payment');
    }

    // chatId and transactionId are different — chatId is stored on the transaction
    const chatId = transaction.chatId || transactionId;

    // Generate QR code using chatId (not transactionId)
    const transactionCode = generateTransactionCode();
    const baseUrl = window.location.origin;
    const qrCodeUrl = `${baseUrl}/messages?chat=${chatId}&code=${transactionCode}`;

    // Check if using mock Stripe
    if (transaction.useMockStripe) {
      console.log('🎭 ===== USING MOCK STRIPE =====');
      console.log('📊 Mock payment details:', {
        sellerId: transaction.sellerId,
        sellerStripeStatus: transaction.sellerStripeStatus || 'not_connected',
        reason: 'Seller has not completed Stripe verification'
      });
      
      const mockPaymentIntentId = `pi_mock_${Date.now()}`;
      const mockClientSecret = `${mockPaymentIntentId}_secret_mock`;

      console.log('📝 Updating transaction to paid status...');
      await updateDoc(transactionRef, {
        status: 'paid',
        paymentStatus: 'escrowed',
        transactionCode,
        qrCodeUrl,
        stripeClientSecret: mockClientSecret,
        stripePaymentIntentId: mockPaymentIntentId,
        useMockStripe: true,
        paidAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      console.log('💬 Adding chat messages...');
      const chatRef = doc(db, 'chats', chatId);

      await updateDoc(chatRef, {
        lastMessage: 'Payment processed (MOCK)',
        lastMessageTime: serverTimestamp(),
        [`unreadCount.${transaction.sellerId}`]: 1
      });

      await addDoc(collection(db, 'chats', chatId, 'messages'), {
        text: `✅ Mock Escrow Payment Confirmed\n\n💰 Your payment of $${transaction.total.toFixed(2)} is securely held (MOCK MODE)`,
        sender: buyerId,
        senderName: buyerName,
        timestamp: serverTimestamp(),
        type: 'escrow_confirmed'
      });

      await addDoc(collection(db, 'chats', chatId, 'messages'), {
        text: `Please let me know where and when to bring the pickup QR code, thanks.`,
        sender: buyerId,
        senderName: buyerName,
        timestamp: serverTimestamp(),
        type: 'buyer_asks_location'
      });

      const visibleTo = undefined;

      await addDoc(collection(db, 'chats', chatId, 'messages'), {
        text: `Payment successful! Your Pickup Kode:`,
        sender: buyerId,
        senderName: buyerName,
        timestamp: serverTimestamp(),
        type: 'payment_qr_code',
        visibleTo: visibleTo,
        purchaseData: {
          transactionCode,
          qrCodeUrl,
          approvedQuantity: transaction.approvedQuantity || transaction.requestedQuantity,
          total: transaction.total
        }
      });

      console.log('✅ Mock payment complete');

      await NotificationService.notifyPaymentReceived(
        transaction.sellerId,
        buyerName,
        transaction.itemName,
        transaction.total,
        transactionId
      );

      return {
        success: true,
        clientSecret: mockClientSecret,
        paymentIntentId: mockPaymentIntentId,
        transactionCode,
        qrCodeUrl,
        isMock: true
      };
    }

    // Real Stripe
    // Real Stripe payment
    console.log('💳 Processing real Stripe payment');
    
    const response = await fetch('/api/create-payment-intent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        paymentMethodId: stripePaymentMethodId,
        transactionId,
        amount: transaction.total,
        buyerId: buyerId,
        buyerName: buyerName
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Payment processing failed');
    }

    const result = await response.json();

    if (result.success) {
      try {
        await NotificationService.notifyPaymentReceived(
          transaction.sellerId,
          buyerName,
          transaction.itemName || 'Your order',
          transaction.total,
          transactionId
        );
      } catch (_) {}

      return {
        success: true,
        transactionCode: result.transactionCode,
        qrCodeUrl: result.qrCodeUrl,
        clientSecret: result.clientSecret,
        paymentIntentId: result.paymentIntentId,
        isMock: false
      };
    }

  } catch (error) {
    console.error('❌ Payment error:', error);
    throw error;
  }
},

  sharePickupLocation: async (transactionId, locationAddress) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('Authentication required');

      const transactionRef = doc(db, 'transactions', transactionId);
      const transactionSnap = await getDoc(transactionRef);
      
      if (!transactionSnap.exists()) {
        throw new Error('Transaction not found');
      }
      
      const transaction = transactionSnap.data();

      if (transaction.sellerId !== currentUser.uid) {
        // Allow shared staff and sub-shop owners to share the pickup location too
        const shopCheckSnap = await getDoc(doc(db, 'shops', transaction.sellerId));
        const shopCheckData = shopCheckSnap.exists() ? shopCheckSnap.data() : null;
        const isSharedMember = !!shopCheckData?.sharedUserMap?.[currentUser.uid];
        const isSubShopOwner = shopCheckData?.ownerId === currentUser.uid;

        if (!isSharedMember && !isSubShopOwner) {
          throw new Error('Only the seller can share pickup location');
        }
      }

      await updateDoc(transactionRef, {
        pickupLocation: locationAddress,
        pickupSharedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      await updateDoc(doc(db, 'chats', transactionId), {
        lastMessage: 'Pickup location shared',
        lastMessageTime: serverTimestamp(),
        [`unreadCount.${transaction.buyerId}`]: 1
      });

      const shopRef = doc(db, 'shops', transaction.sellerId);
      const shopSnap = await getDoc(shopRef);
      const shopData = shopSnap.data();

      await addDoc(collection(db, 'chats', transactionId, 'messages'), {
        text: `The item's pickup location is:\n\n${locationAddress}\n\nLet me know a good time you plan on arriving.`,
        sender: currentUser.uid,
        senderName: shopData.name || currentUser.displayName || currentUser.email,
        timestamp: serverTimestamp(),
        type: 'pickup_location_shared'
      });

      await NotificationService.notifyPickupLocationShared(
  transaction.buyerId,
  shopData.name || '',
  locationAddress,
  transactionId
);

      return { success: true };
    } catch (error) {
      console.error('Error sharing pickup location:', error);
      throw error;
    }
  },

  // ─── Seller's pickup payment handles (Zelle/Venmo/other) ──────────────────
  // Stored on the shop doc so they apply across every chat with this seller.
  updatePaymentHandles: async (sellerId, { zelleId = '', venmoId = '', other = '' } = {}) => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('Authentication required');
    if (currentUser.uid !== sellerId) throw new Error('Only the seller can update payment handles');

    await updateDoc(doc(db, 'shops', sellerId), {
      paymentHandles: {
        zelleId: zelleId.trim(),
        venmoId: venmoId.trim(),
        other: other.trim(),
      },
    });
    return { success: true };
  },

  // Buyer picks how they'll pay the seller at pickup (zelle | venmo | cash | card)
  chooseBuyerPaymentMethod: async (transactionId, chatId, method) => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('Authentication required');

    const label = PAYMENT_METHOD_LABELS[method] || method;
    const cId = chatId || transactionId;

    await updateDoc(doc(db, 'transactions', transactionId), {
      buyerPaymentChoice: method,
      updatedAt: serverTimestamp(),
    });
    await updateDoc(doc(db, 'chats', cId), {
      buyerPaymentChoice: method,
      lastMessage: `Buyer chose ${label} for pickup payment`,
      lastMessageTime: serverTimestamp(),
    });
    await addDoc(collection(db, 'chats', cId, 'messages'), {
      text: `💳 Buyer will pay via ${label} at pickup.`,
      sender: 'system',
      senderName: 'System',
      timestamp: serverTimestamp(),
      type: 'payment_method_chosen',
    });

    return { success: true };
  },

  // Step 4: Complete transaction - UPDATED for services
// src/services/TransactionService.js - completeTransaction function
completeTransaction: async (transactionId, code) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('Authentication required');

    // Get transaction first
    const transactionRef = doc(db, 'transactions', transactionId);
    const transactionSnap = await getDoc(transactionRef);
    
    if (!transactionSnap.exists()) {
      throw new Error('Transaction not found');
    }
    
    const transaction = transactionSnap.data();
    
    // Verify we have chatId (for validation only)
    let chatId = transaction.chatId;
    if (!chatId) {
      console.error('❌ No chatId found in transaction!');
      throw new Error('Transaction is missing chatId - cannot complete');
    }

    console.log('✅ Found chatId:', chatId);

    // On localhost the Vercel serverless API is unavailable — write Firestore directly
    const isLocalhost =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    if (isLocalhost || transaction.useMockStripe) {
      if (transaction.transactionCode !== code) throw new Error('Invalid verification code');
      if (transaction.status === 'completed') throw new Error('Transaction already completed');

      await updateDoc(transactionRef, {
        status: 'completed',
        paymentStatus: 'released',
        completedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        completionMethod: 'mock_local',
      });

      await updateDoc(doc(db, 'chats', chatId), {
        status: 'completed',
        transactionStatus: 'completed',
        lastMessage: 'Transaction completed',
        lastMessageTime: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const qty = transaction.approvedQuantity || transaction.requestedQuantity || 1;
      await setDoc(doc(db, 'users', transaction.sellerId), {
        itemsSold: increment(qty),
        totalEarnings: increment(parseFloat(transaction.sellerPayout || transaction.total || 0)),
      }, { merge: true });

      if (transaction.buyerId && !transaction.buyerId.startsWith('guest_')) {
        await setDoc(doc(db, 'users', transaction.buyerId), {
          itemsBought: increment(qty),
        }, { merge: true });
      }

      return {
        success: true,
        message: 'Transaction completed successfully',
        quantity: qty,
        totalAmount: transaction.total,
        sellerPayout: transaction.sellerPayout,
        isService: transaction.isService || false,
      };
    }

    // Call API to complete (API handles everything)
    const response = await fetch(`${BACKEND_URL}/complete-transaction`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        transactionId,
        verificationCode: code
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Transaction completion failed');
    }

    // Firestore listener updates the UI automatically
    const quantity = transaction.approvedQuantity || transaction.requestedQuantity;
    const isService = transaction.isService || false;

    // Notify both parties of completion
    try {
      await Promise.all([
        NotificationService.notifyTransactionCompleted(
          transaction.sellerId, true, transaction.itemName || 'Order', transaction.sellerPayout || transaction.total, transactionId
        ),
        NotificationService.notifyTransactionCompleted(
          transaction.buyerId, false, transaction.itemName || 'Order', transaction.total, transactionId
        ),
      ]);
    } catch (_) {}

    return {
      success: true,
      message: 'Transaction completed successfully',
      quantity,
      totalAmount: transaction.total,
      sellerPayout: transaction.sellerPayout,
      isService
    };
  } catch (error) {
    console.error('Error completing transaction:', error);
    throw error;
  }
},

  confirmCashPayment: async (transactionId, method = 'cash') => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('Authentication required');

      const label = PAYMENT_METHOD_LABELS[method] || 'Cash';
      const transactionRef = doc(db, 'transactions', transactionId);
      const snap = await getDoc(transactionRef);
      if (!snap.exists()) throw new Error('Transaction not found');
      const data = snap.data();
      const chatId = data.chatId;

      await updateDoc(transactionRef, {
        status: 'completed',
        paymentStatus: `${method}_collected`,
        completedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      if (chatId) {
        await updateDoc(doc(db, 'chats', chatId), {
          status: 'completed',
          transactionStatus: 'completed',
          lastMessage: `${label} payment confirmed. Order complete.`,
          lastMessageTime: serverTimestamp()
        });
        await addDoc(collection(db, 'chats', chatId, 'messages'), {
          text: `✅ ${label} collected. Order marked complete.`,
          sender: 'system',
          senderName: 'System',
          timestamp: serverTimestamp(),
          type: 'order_completed'
        });
      }
      // Notify both parties of cash completion
      try {
        await Promise.all([
          NotificationService.notifyTransactionCompleted(
            currentUser.uid, true, data.itemName || 'Order', data.sellerPayout || data.total, transactionId
          ),
          NotificationService.notifyTransactionCompleted(
            data.buyerId, false, data.itemName || 'Order', data.total, transactionId
          ),
        ]);
      } catch (_) {}

      return { success: true };
    } catch (error) {
      console.error('❌ confirmCashPayment error:', error);
      throw error;
    }
  },

  // Completes a self-order immediately after payment (no QR scan needed)
  completeSelfOrder: async (transactionId) => {
    try {
      const snap = await getDoc(doc(db, 'transactions', transactionId));
      if (!snap.exists()) throw new Error('Transaction not found');
      const data = snap.data();
      if (!data.isSelfOrder) throw new Error('Not a self-order');
      const code = data.transactionCode;
      if (!code) throw new Error('Transaction code not yet available');
      return await TransactionService.completeTransaction(transactionId, code);
    } catch (error) {
      console.error('❌ completeSelfOrder error:', error);
      throw error;
    }
  },

  confirmCardOnPickup: async (transactionId) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('Authentication required');

      const transactionRef = doc(db, 'transactions', transactionId);
      const snap = await getDoc(transactionRef);
      if (!snap.exists()) throw new Error('Transaction not found');
      const data = snap.data();
      const chatId = data.chatId;

      await updateDoc(transactionRef, {
        status: 'completed',
        paymentStatus: 'card_collected',
        completedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      if (chatId) {
        await updateDoc(doc(db, 'chats', chatId), {
          status: 'completed',
          transactionStatus: 'completed',
          lastMessage: 'Card payment collected. Order complete.',
          lastMessageTime: serverTimestamp()
        });
        await addDoc(collection(db, 'chats', chatId, 'messages'), {
          text: '✅ Card payment collected. Order marked complete.',
          sender: 'system',
          senderName: 'System',
          timestamp: serverTimestamp(),
          type: 'order_completed'
        });
      }
      return { success: true };
    } catch (error) {
      console.error('❌ confirmCardOnPickup error:', error);
      throw error;
    }
  },

  // Add item to an existing active order from the same seller.
  // Returns { chatId } if merged into existing chat, null if no existing chat found.
  // NOTE: services always start their own transaction — never merged.
  addItemToActiveOrder: async (itemId, sellerId, unitPrice, quantity, isService, isDigital, customization = null) => {
    const buyer = auth.currentUser;
    if (!buyer) throw new Error('Please sign in to continue');

    // Fetch item info
    const shopSnap = await getDoc(doc(db, 'shops', sellerId));
    if (!shopSnap.exists()) throw new Error('Shop not found');
    const shopData = shopSnap.data();

    let item = (isService ? shopData.services : shopData.items)?.find(p => p.id === itemId);
    if (!item) item = shopData.items?.find(p => p.id === itemId);
    if (!item) item = shopData.services?.find(p => p.id === itemId);
    if (!item) throw new Error('Item not found');

    const newCartItem = {
      itemId,
      itemName: item.name,
      itemImage: item.images?.[0] || null,
      unitPrice: parseFloat(unitPrice),
      quantity: parseInt(quantity),
      isService: !!isService,
      isDigital: !!isDigital,
      customization: customization || null,
    };

    const chatsRef = collection(db, 'chats');

    // 0. Still a draft cart the buyer hasn't sent yet (pending_send + isCart) —
    // same merge behaviour as addToCart, so buying again before the order is
    // even sent doesn't spin up a second parallel order.
    const draftCartSnap = await getDocs(query(
      chatsRef,
      where('buyerId', '==', buyer.uid),
      where('sellerId', '==', sellerId),
      where('status', '==', 'pending_send'),
      where('isCart', '==', true),
      limit(5)
    ));

    if (!draftCartSnap.empty) {
      // Pick the most recently updated draft cart if there are somehow multiple
      const sortedCarts = draftCartSnap.docs.slice().sort((a, b) => {
        const aT = a.data().updatedAt?.toMillis?.() || a.data().lastMessageTime?.toMillis?.() || 0;
        const bT = b.data().updatedAt?.toMillis?.() || b.data().lastMessageTime?.toMillis?.() || 0;
        return bT - aT;
      });
      const cartDoc = sortedCarts[0];
      const cartId = cartDoc.id;
      const cartData = cartDoc.data();

      const existingItems = cartData.cartItems || [];
      const existingIdx = existingItems.findIndex(ci => ci.itemId === itemId);
      const updatedItems = existingIdx >= 0
        ? existingItems.map((ci, i) => i === existingIdx ? { ...ci, quantity: ci.quantity + parseInt(quantity) } : ci)
        : [...existingItems, newCartItem];

      const newTotals = TransactionService.calculateCartTotal(updatedItems);

      await updateDoc(doc(db, 'chats', cartId), {
        cartItems: updatedItems,
        ...newTotals,
        lastMessage: `Added: ${item.name}`,
        lastMessageTime: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return { chatId: cartId };
    }

    // 1. Pre-payment: pending_seller_acceptance or seller_accepted — always reuse
    // the most recent open order from this store regardless of age. One chat per
    // buyer-seller pair is the invariant; we never want a second parallel chat.
    // No orderBy to avoid a composite index; sort client-side instead.
    const prePaySnap = await getDocs(query(
      chatsRef,
      where('buyerId', '==', buyer.uid),
      where('sellerId', '==', sellerId),
      where('status', 'in', ['pending_seller_acceptance', 'seller_accepted']),
      limit(5)
    ));

    if (!prePaySnap.empty) {
      // Most recently updated first
      const sortedDocs = prePaySnap.docs.slice().sort((a, b) => {
        const aT = a.data().updatedAt?.toMillis?.() || a.data().lastMessageTime?.toMillis?.() || 0;
        const bT = b.data().updatedAt?.toMillis?.() || b.data().lastMessageTime?.toMillis?.() || 0;
        return bT - aT;
      });
      const chatDoc = sortedDocs[0];
      const chatId = chatDoc.id;
      const chatData = chatDoc.data();

      const existingItems = chatData.cartItems || [];

      const existingIdx = existingItems.findIndex(ci => ci.itemId === itemId);
      const updatedItems = existingIdx >= 0
        ? existingItems.map((ci, i) => i === existingIdx ? { ...ci, quantity: ci.quantity + parseInt(quantity) } : ci)
        : [...existingItems, newCartItem];

      const newTotals = TransactionService.calculateCartTotal(updatedItems);

      await updateDoc(doc(db, 'chats', chatId), {
        cartItems: updatedItems,
        ...newTotals,
        lastMessage: `Added: ${item.name}`,
        lastMessageTime: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const txId = chatData.transactionId || chatId;
      try {
        const txSnap = await getDoc(doc(db, 'transactions', txId));
        if (txSnap.exists()) {
          await updateDoc(doc(db, 'transactions', txId), {
            cartItems: updatedItems,
            ...newTotals,
            updatedAt: serverTimestamp(),
          });
        }
      } catch (e) { /* non-fatal */ }

      await addDoc(collection(db, 'chats', chatId, 'messages'), {
        text: `🛒 Added to order: ${quantity}x ${item.name} ($${(parseFloat(unitPrice) * quantity).toFixed(2)})`,
        sender: 'system',
        senderName: 'System',
        timestamp: serverTimestamp(),
        type: 'item_added',
      });

      return { chatId };
    }

    // 2. Order already paid or fully completed → start a new round in the same chat
    const paidSnap = await getDocs(query(
      chatsRef,
      where('buyerId', '==', buyer.uid),
      where('sellerId', '==', sellerId),
      where('status', 'in', ['paid', 'completed']),
      limit(5)
    ));

    if (!paidSnap.empty) {
      const chatDoc = paidSnap.docs[0];
      const chatId = chatDoc.id;
      const chatData = chatDoc.data();

      const currentPaidRounds = chatData.paidRounds || [];
      const archivedRound = {
        items: chatData.cartItems || [],
        total: chatData.total || 0,
        roundNumber: currentPaidRounds.length + 1,
      };

      const newCartItems = [newCartItem];
      const newTotals = TransactionService.calculateCartTotal(newCartItems);

      await updateDoc(doc(db, 'chats', chatId), {
        cartItems: newCartItems,
        paidRounds: [...currentPaidRounds, archivedRound],
        status: 'pending_seller_acceptance',
        transactionStatus: 'pending_seller_acceptance',
        ...newTotals,
        lastMessage: `New item added: ${item.name} — awaiting seller`,
        lastMessageTime: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const txId = chatData.transactionId || chatId;
      try {
        const txSnap = await getDoc(doc(db, 'transactions', txId));
        if (txSnap.exists()) {
          await updateDoc(doc(db, 'transactions', txId), {
            cartItems: newCartItems,
            paidRounds: [...currentPaidRounds, archivedRound],
            status: 'pending_seller_acceptance',
            paymentStatus: 'not_paid',
            ...newTotals,
            updatedAt: serverTimestamp(),
          });
        }
      } catch (e) { /* non-fatal */ }

      await addDoc(collection(db, 'chats', chatId, 'messages'), {
        text: `🛒 New items added after payment!\n${quantity}x ${item.name} — $${newTotals.total.toFixed(2)}\n\nAwaiting seller acceptance.`,
        sender: 'system',
        senderName: 'System',
        timestamp: serverTimestamp(),
        type: 'new_order_round',
      });

      return { chatId };
    }

    // 3. No existing order found
    return null;
  },

  // Buyer removes one item from a still-editable (pre-payment) order/cart
  removeItemFromOrder: async (chatId, itemId) => {
    const buyer = auth.currentUser;
    if (!buyer) throw new Error('Please sign in to continue');

    const chatRef = doc(db, 'chats', chatId);
    const chatSnap = await getDoc(chatRef);
    if (!chatSnap.exists()) throw new Error('Order not found');
    const chatData = chatSnap.data();

    const existingItems = chatData.cartItems || [];
    const updatedItems = existingItems.filter(ci => ci.itemId !== itemId);
    if (updatedItems.length === 0) throw new Error('An order needs at least one item');

    const newTotals = TransactionService.calculateCartTotal(updatedItems);

    await updateDoc(chatRef, {
      cartItems: updatedItems,
      ...newTotals,
      lastMessage: 'Buyer removed an item from the order',
      lastMessageTime: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    if (chatData.transactionId) {
      try {
        const txRef = doc(db, 'transactions', chatData.transactionId);
        const txSnap = await getDoc(txRef);
        if (txSnap.exists()) {
          await updateDoc(txRef, { cartItems: updatedItems, ...newTotals, updatedAt: serverTimestamp() });
        }
      } catch (e) { /* non-fatal */ }
    }

    return { success: true };
  },

  // Buyer adjusts an item's quantity any time the order is still editable (draft,
  // awaiting seller acceptance, or already accepted but unpaid). Mirrors
  // removeItemFromOrder's direct-write pattern — handles both multi-item cart
  // orders and single-item pendingPurchase orders. If the seller already approved
  // a quantity, that field is kept in sync too since it's what the UI displays.
  updateItemQuantity: async (chatId, itemId, newQuantity) => {
    const buyer = auth.currentUser;
    if (!buyer) throw new Error('Please sign in to continue');

    const chatRef = doc(db, 'chats', chatId);
    const chatSnap = await getDoc(chatRef);
    if (!chatSnap.exists()) throw new Error('Order not found');
    const chatData = chatSnap.data();

    if (chatData.cartItems?.length) {
      const item = chatData.cartItems.find(ci => ci.itemId === itemId);
      if (!item) throw new Error('Item not found in order');
      const clampedQty = Math.max(1, Math.min(parseInt(newQuantity) || 1, item.availableQuantity || 999));
      const updatedItems = chatData.cartItems.map(ci =>
        ci.itemId === itemId ? { ...ci, quantity: clampedQty } : ci
      );
      const newTotals = TransactionService.calculateCartTotal(updatedItems);

      await updateDoc(chatRef, {
        cartItems: updatedItems,
        ...newTotals,
        lastMessage: 'Buyer updated item quantity',
        lastMessageTime: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      if (chatData.transactionId) {
        try {
          const txRef = doc(db, 'transactions', chatData.transactionId);
          const txSnap = await getDoc(txRef);
          if (txSnap.exists()) {
            await updateDoc(txRef, { cartItems: updatedItems, ...newTotals, updatedAt: serverTimestamp() });
          }
        } catch (e) { /* non-fatal */ }
      }
    } else {
      const pp = chatData.pendingPurchase;
      if (!pp) throw new Error('Order not found');
      // Services are priced hourly/daily/flat — their total already reflects
      // duration, not a per-unit quantity, so re-deriving cost as unitPrice×qty
      // here would silently corrupt the price. Duration is edited via
      // updateItemCustomization instead.
      if (pp.isService) throw new Error('Quantity can\'t be changed for a service booking');
      const clampedQty = Math.max(1, Math.min(parseInt(newQuantity) || 1, pp.availableQuantity || 999));
      const newCosts = calculateTransactionCosts(pp.unitPrice, clampedQty);

      // The transaction doc is the authoritative source the UI reads quantity
      // from (it's preferred over chat.pendingPurchase whenever both exist), so
      // that write must succeed — not be treated as a best-effort mirror — or
      // the displayed quantity silently falls back to the old value.
      if (!chatData.transactionId) throw new Error('Order is missing its transaction record');
      const txRef = doc(db, 'transactions', chatData.transactionId);
      const txSnap = await getDoc(txRef);
      if (!txSnap.exists()) throw new Error('Order not found');
      const txUpdates = { requestedQuantity: clampedQty, ...newCosts, updatedAt: serverTimestamp() };
      if (txSnap.data().approvedQuantity != null) {
        txUpdates.approvedQuantity = clampedQty;
      }
      await updateDoc(txRef, txUpdates);

      const chatUpdates = {
        'pendingPurchase.requestedQuantity': clampedQty,
        ...newCosts,
        lastMessage: 'Buyer updated item quantity',
        lastMessageTime: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      if (pp.approvedQuantity != null) {
        chatUpdates['pendingPurchase.approvedQuantity'] = clampedQty;
      }
      try {
        await updateDoc(chatRef, chatUpdates);
      } catch (e) { /* non-fatal — transaction doc (the authoritative source) already updated */ }
    }

    return { success: true };
  },

  // Buyer edits an item's selected attributes / add-ons / booking-form answers
  // (the "orderDetails" the order form collected). Same editable window as
  // updateItemQuantity — handles both multi-item cart orders and single-item
  // pendingPurchase/transaction orders, where the data lives on the transaction
  // doc itself rather than per cart item.
  // `newTotalPrice`, when given (hourly/daily service duration changed), also
  // updates the actual charged unitPrice/costs — not just the stored form
  // answer — since that's what the booking is priced from. Services never
  // appear in cartItems (see addItemToActiveOrder), so this only applies to
  // the single-item branch below.
  updateItemCustomization: async (chatId, itemId, orderDetails, newTotalPrice = null) => {
    const buyer = auth.currentUser;
    if (!buyer) throw new Error('Please sign in to continue');

    const chatRef = doc(db, 'chats', chatId);
    const chatSnap = await getDoc(chatRef);
    if (!chatSnap.exists()) throw new Error('Order not found');
    const chatData = chatSnap.data();

    if (chatData.cartItems?.length) {
      const item = chatData.cartItems.find(ci => ci.itemId === itemId);
      if (!item) throw new Error('Item not found in order');
      const updatedItems = chatData.cartItems.map(ci =>
        ci.itemId === itemId
          ? { ...ci, customization: { ...(ci.customization || {}), orderDetails } }
          : ci
      );

      await updateDoc(chatRef, {
        cartItems: updatedItems,
        lastMessage: 'Buyer updated order options',
        lastMessageTime: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      if (chatData.transactionId) {
        try {
          const txRef = doc(db, 'transactions', chatData.transactionId);
          const txSnap = await getDoc(txRef);
          if (txSnap.exists()) {
            await updateDoc(txRef, { cartItems: updatedItems, updatedAt: serverTimestamp() });
          }
        } catch (e) { /* non-fatal */ }
      }
    } else {
      if (!chatData.transactionId) throw new Error('Order not found');
      const txRef = doc(db, 'transactions', chatData.transactionId);
      const txSnap = await getDoc(txRef);
      if (!txSnap.exists()) throw new Error('Order not found');
      const txData = txSnap.data();
      const existingCustomization = txData.customization || {};

      const txUpdates = {
        customization: { ...existingCustomization, orderDetails },
        updatedAt: serverTimestamp(),
      };
      if (newTotalPrice != null && !isNaN(newTotalPrice)) {
        const newCosts = calculateTransactionCosts(newTotalPrice, 1);
        Object.assign(txUpdates, { unitPrice: parseFloat(newTotalPrice.toFixed(2)), ...newCosts });
        if (txData.approvedQuantity != null) txUpdates.approvedQuantity = 1;
      }
      await updateDoc(txRef, txUpdates);

      const chatUpdates = {
        lastMessage: 'Buyer updated order options',
        lastMessageTime: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      if (newTotalPrice != null && !isNaN(newTotalPrice)) {
        chatUpdates['pendingPurchase.unitPrice'] = parseFloat(newTotalPrice.toFixed(2));
      }
      await updateDoc(chatRef, chatUpdates).catch(() => {});
    }

    return { success: true };
  },

  async withdrawPayment(transactionId) {
  try {
    console.log('🔄 Withdrawing payment for transaction:', transactionId);
    
    // ✅ Call the cancel API instead of refund API
    const response = await fetch('/api/cancel-payment-intent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transactionId })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to cancel payment');
    }

    const result = await response.json();
    console.log('✅ Payment withdrawn successfully:', result);
    
    return result;

  } catch (error) {
    console.error('❌ Error withdrawing payment:', error);
    throw error;
  }
},

  // ─── Service progress tracking ────────────────────────────────────────────
  // Called by the service performer (driver / pro) to advance live state.
  // `progress` values: 'en_route' | 'picking_up' | 'dropping_off' |
  //                    'job_started' | 'loading' | 'delivering' | 'service_started'
  updateServiceProgress: async (transactionId, progress) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('Authentication required');

      const transRef  = doc(db, 'transactions', transactionId);
      const transSnap = await getDoc(transRef);
      if (!transSnap.exists()) throw new Error('Transaction not found');

      const data   = transSnap.data();
      const chatId = data.chatId || transactionId;

      // Timestamp markers per milestone
      const extra = {};
      if (progress === 'en_route')      extra.serviceStartedAt = serverTimestamp();
      if (progress === 'job_started')   extra.serviceStartedAt = serverTimestamp();
      if (progress === 'service_started') extra.serviceStartedAt = serverTimestamp();
      if (progress === 'loading')       extra.serviceStartedAt = serverTimestamp();
      if (progress === 'picking_up')    extra.pickupAt          = serverTimestamp();
      if (progress === 'dropping_off')  extra.dropoffAt         = serverTimestamp();
      if (progress === 'delivering')    extra.dropoffAt         = serverTimestamp();

      await updateDoc(transRef, {
        serviceProgress: progress,
        ...extra,
        updatedAt: serverTimestamp()
      });

      const progressLabels = {
        en_route:        '🚗 Driver is on the way',
        picking_up:      '🛑 Driver arrived at pickup location',
        dropping_off:    '🏁 Driver is en route to destination',
        job_started:     '🔧 Service has started',
        loading:         '📦 Loading items at pickup location',
        delivering:      '🚚 In transit to destination',
        service_started: '▶️ Service has started',
      };
      const msg = progressLabels[progress] || `Status updated: ${progress}`;

      await Promise.all([
        addDoc(collection(db, 'chats', chatId, 'messages'), {
          text: msg,
          sender: 'system',
          senderName: 'System',
          timestamp: serverTimestamp(),
          type: 'service_progress',
          serviceProgress: progress
        }),
        updateDoc(doc(db, 'chats', chatId), {
          lastMessage: msg,
          lastMessageTime: serverTimestamp(),
          updatedAt: serverTimestamp()
        })
      ]);

      return { success: true, serviceProgress: progress };
    } catch (err) {
      console.error('updateServiceProgress error:', err);
      throw err;
    }
  },

  // ─── Job claim transaction ────────────────────────────────────────────────
  // Used when a driver/worker claims a 'need'-tab service listing.
  // Roles are intentionally FLIPPED from the normal model:
  //   sellerId = claimant (driver/worker) — they get paid, appear in Selling tab
  //   buyerId  = shop owner (dispatch)    — they pay,     appear in Buying tab
  claimJobTransaction: async (item, shopId, customization = null) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('Please sign in to continue');

      const driverUID = currentUser.uid;

      // Dispatch = shop owner
      const shopRef  = doc(db, 'shops', shopId);
      const shopSnap = await getDoc(shopRef);
      if (!shopSnap.exists()) throw new Error('Shop not found');
      const shopData    = shopSnap.data();
      const dispatchUID = shopId; // shop doc ID == owner UID

      if (driverUID === dispatchUID) throw new Error('You cannot claim your own job listing');

      // Driver profile
      const driverSnap = await getDoc(doc(db, 'users', driverUID));
      const driverData = driverSnap.exists() ? driverSnap.data() : {};
      const driverName = driverData.displayName || currentUser.displayName || 'Driver';
      const driverPic  = driverData.profilePic  || null;

      // Driver's Stripe account (driver is the payee — must be verified before work starts)
      const driverShopSnap = await getDoc(doc(db, 'shops', driverUID));
      const driverShopData = driverShopSnap.exists() ? driverShopSnap.data() : {};
      const driverStripeAccountId = driverShopData.stripeAccountId || null;
      const driverStripeReady     = !!(driverShopData.stripeReady && driverShopData.chargesEnabled && driverShopData.payoutsEnabled);
      const driverStripeStatus    = driverStripeReady ? 'active' : (driverShopData.stripeAccountStatus || 'not_connected');

      const unitPrice  = parseFloat(item.price) || 0;
      const costs      = calculateTransactionCosts(unitPrice, 1);

      const cartItem = {
        itemId:    item.id,
        itemName:  item.name,
        itemImage: item.images?.[0] || null,
        unitPrice,
        quantity:  1,
        isService: true,
        jobType:   item.jobType || null,
      };

      const transaction = {
        itemId:    item.id,
        itemName:  item.name,
        itemImage: item.images?.[0] || null,
        cartItems: [cartItem],  // ← needed for UnifiedCartWindow to render
        isService:  true,
        isClaim:    true,         // ← job claim flag
        jobType:    item.jobType    || null,
        serviceTab: 'need',
        serviceProgress: null,
        // Ride / location fields
        rideDateTime:           item.rideDateTime           || null,
        pickupAddress:          item.pickupAddress          || null,
        pickupCoordinates:      item.pickupCoordinates      || null,
        destinationAddress:     item.destinationAddress     || null,
        destinationCoordinates: item.destinationCoordinates || null,
        passengers:             item.passengers             || null,
        estimatedDistance:      item.estimatedDistance      || null,
        vehicleType:            item.vehicleType            || null,
        // FLIPPED roles: driver is seller, dispatch is buyer
        buyerId:   driverUID,
        buyerName: driverName,
        sellerId:  dispatchUID,
        sellerName: shopData.name || '',
        currency: shopData.currency || 'USD',
        roles: {
          [driverUID]:   'seller',  // driver → Selling tab
          [dispatchUID]: 'buyer',   // dispatch → Buying tab
        },
        participants:    [driverUID, dispatchUID],
        isSelfOrder:     false,
        unitPrice,
        requestedQuantity: 1,
        approvedQuantity:  null,
        ...costs,
        useMockStripe:  true,  // job-claim payment handled on completion
        // Driver's Stripe (driver is the payee — stored so dispatch can verify before accepting)
        sellerStripeAccountId: driverStripeAccountId,
        sellerStripeStatus:    driverStripeStatus,
        driverStripeReady,
        status:        'pending_seller_acceptance',
        paymentStatus: 'not_paid',
        meetupType:    'inperson',
        transactionCode: null,
        customization:   customization || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const transactionRef = await addDoc(collection(db, 'transactions'), transaction);
      const transactionId  = transactionRef.id;
      await updateDoc(transactionRef, { chatId: transactionId });

      const orderNumber = transactionId.slice(-6).toUpperCase();

      const chatDoc = {
        transactionId,
        itemId:    item.id,
        itemName:  item.name,
        itemImage: item.images?.[0] || null,
        cartItems: [cartItem],  // ← needed for UnifiedCartWindow to render
        isService: true,
        isClaim:   true,
        buyerId:   driverUID,
        buyerName: driverName,
        sellerId:  dispatchUID,
        sellerName: shopData.name || '',
        roles: {
          [driverUID]:   'seller',
          [dispatchUID]: 'buyer',
        },
        participants: [driverUID, dispatchUID],
        isSelfOrder:  false,
        title: `Job Claim #${orderNumber}`,
        chatImage: driverPic || `https://api.dicebear.com/7.x/avataaars/svg?seed=${driverUID}`,
        lastMessage: `Claim submitted: ${item.name}`,
        lastMessageTime: serverTimestamp(),
        unreadCount: {
          [driverUID]:   0,
          [dispatchUID]: 1,  // notify dispatch
        },
        pendingPurchase: {
          itemId:    item.id,
          isService: true,
          itemName:  item.name,
          unitPrice,
          requestedQuantity: 1,
          ...costs,
          status:    'pending_seller_acceptance',
          buyerName: driverName,
        },
      };

      await setDoc(doc(db, 'chats', transactionId), chatDoc);

      await addDoc(collection(db, 'chats', transactionId, 'messages'), {
        text: `I'd like to claim this job: ${item.name}. Please confirm my assignment.`,
        sender: driverUID,
        senderName: driverName,
        timestamp: serverTimestamp(),
        type: 'job_claim',
        claimData: { jobType: item.jobType || null, unitPrice },
      });

      // Visible application summary — dispatch sees pro's details
      const claimDetailsText = formatOrderDetailsMessage(customization?.orderDetails);
      if (claimDetailsText) {
        const claimSummaryLines = [
          `📋 Job Application: ${item.name}`,
          claimDetailsText.trim(),
        ].join('\n');
        await addDoc(collection(db, 'chats', transactionId, 'messages'), {
          text: claimSummaryLines,
          sender: driverUID,
          senderName: driverName,
          timestamp: serverTimestamp(),
          type: 'order_summary',
        });
      }

      // Notify dispatch of the new claim
      await NotificationService.notifyNewOrderRequest(
        dispatchUID,
        driverName,
        [{ itemName: item.name, quantity: 1, unitPrice }],
        costs.total,
        transactionId,
        false,
        shopData.name || ''
      );

      return { transactionId, status: 'pending_seller_acceptance' };
    } catch (err) {
      console.error('claimJobTransaction error:', err);
      throw err;
    }
  },

  // Auto-captures escrowed payment and closes the transaction when service is done.
  completeServiceTransaction: async (transactionId) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('Authentication required');

      const transRef  = doc(db, 'transactions', transactionId);
      const transSnap = await getDoc(transRef);
      if (!transSnap.exists()) throw new Error('Transaction not found');

      const data   = transSnap.data();
      const chatId = data.chatId || transactionId;
      const now    = new Date();

      // Build trip summary
      const startedAt   = data.serviceStartedAt?.toDate?.() || now;
      const durationMin = Math.max(1, Math.round((now - startedAt) / 60000));
      const tripSummary = {
        durationMinutes: durationMin,
        distance:        data.estimatedDistance || null,
        completionTime:  now.toISOString(),
        amount:          data.total || 0
      };

      await updateDoc(transRef, {
        serviceProgress:   'trip_complete',
        tripCompletedAt:   serverTimestamp(),
        tripSummary,
        updatedAt:         serverTimestamp()
      });

      // Use stored QR/verification code to trigger Stripe capture via existing API
      const code = data.transactionCode;
      if (code) {
        return await TransactionService.completeTransaction(transactionId, code);
      }

      // Fallback: direct Firestore close (mock / no-Stripe flows)
      await updateDoc(transRef, {
        status:        'completed',
        paymentStatus: 'released',
        completedAt:   serverTimestamp(),
        updatedAt:     serverTimestamp()
      });

      await Promise.all([
        addDoc(collection(db, 'chats', chatId, 'messages'), {
          text: `✅ Service completed! $${(data.total || 0).toFixed(2)} released.`,
          sender: 'system', senderName: 'System',
          timestamp: serverTimestamp(),
          type: 'service_completed'
        }),
        updateDoc(doc(db, 'chats', chatId), {
          status: 'completed', transactionStatus: 'completed',
          lastMessage: 'Service completed — transaction closed.',
          lastMessageTime: serverTimestamp(),
          updatedAt: serverTimestamp()
        })
      ]);

      return { success: true };
    } catch (err) {
      console.error('completeServiceTransaction error:', err);
      throw err;
    }
  },
};



function generateTransactionCode() {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = 'KODE-';
  
  for (let i = 0; i < 6; i++) {
    code += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  
  return code;
}